import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import {
  IoCloseSharp,
  IoTrashOutline,
  IoEyeOutline,
  IoEyeOffOutline,
  IoLockClosedOutline,
  IoLockOpenOutline,
  IoCopyOutline,
  IoLayersOutline,
  IoUnlinkOutline,
  IoResizeOutline,
  IoGridOutline,
  IoArrowUndoOutline,
  IoArrowRedoOutline,
} from 'react-icons/io5';
import { v4 as uuidv4 } from 'uuid';
import { EditorContext } from '@/features/editor/context/editor-context';
import { DeviceType } from '@/utils/definitions';

type PanelType = 'layers' | 'canvasSize' | null;

const MIN_MARQUEE_DRAG = 4;
const TEXT_LINE_HEIGHT = 8;
const TEXT_LINE_GAP = 8;
const TEXT_PADDING_Y = 10;
const TEXT_BAR_WIDTH_RATIO = 0.8;
const TEXT_BAR_LAST_WIDTH_RATIO = 0.55;
const SKETCH_PADDING = 4;

const calculatePointsBoundingBox = (points: { x: number; y: number }[]) => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  points.forEach((p) => {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  });
  return { minX, minY, maxX, maxY };
};

interface BlockDefinition {
  type: string;
  label: string;
  defaultWidth: number;
  defaultHeight: number;
  fillColor: string;
  strokeColor: string;
  icon: string | React.ReactNode;
  renderContent: (width: number, height: number) => React.ReactNode;
}

const renderHeaderContent = (width: number, height: number, text?: string) => (
  <WireframeHeader style={{ width, height }}>
    {text ? (
      <span className="header-custom-text">{text}</span>
    ) : (
      <div className="header-text" />
    )}
  </WireframeHeader>
);

const renderNavContent = (width: number, height: number) => (
  <WireframeNav style={{ width, height }}>
    <div className="nav-item active" />
    <div className="nav-item" />
    <div className="nav-item" />
    <div className="nav-item" />
  </WireframeNav>
);

const renderImageContent = (width: number, height: number) => (
  <WireframeImage style={{ width, height }}>
    <div className="image-icon">
      <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
      </svg>
    </div>
  </WireframeImage>
);

const buildTextLineCount = (height: number) => {
  const availableH = height - TEXT_PADDING_Y * 2;
  return Math.max(
    1,
    Math.floor(availableH / (TEXT_LINE_HEIGHT + TEXT_LINE_GAP)),
  );
};

const renderTextContent = (width: number, height: number) => (
  <WireframeText style={{ width, height }}>
    {Array.from({ length: buildTextLineCount(height) }, (_, i) => (
      <div key={i} className="text-row">
        <div className="text-bar" />
      </div>
    ))}
  </WireframeText>
);

const renderButtonContent = (width: number, height: number, text?: string) => (
  <WireframeButton style={{ width, height }}>
    <span>{text || `Button`}</span>
  </WireframeButton>
);

const renderProductContent = (width: number, height: number) => (
  <WireframeProduct style={{ width, height }}>
    <div className="product-image">
      <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
      </svg>
    </div>
    <div className="product-info">
      <div className="product-title" />
      <div className="product-price" />
      <div className="product-button" />
    </div>
  </WireframeProduct>
);

const ACCORDION_ITEM_HEIGHT = 34;

const buildAccordionItemCount = (height: number) =>
  Math.max(2, Math.floor(height / ACCORDION_ITEM_HEIGHT));

const renderAccordionContent = (width: number, height: number) => {
  const itemCount = buildAccordionItemCount(height);
  return (
    <WireframeAccordion style={{ width, height }}>
      {Array.from({ length: itemCount }, (_, i) => (
        <div key={i} className={`accordion-item${i > 0 ? ` collapsed` : ``}`}>
          <div className="accordion-header">
            <div className="accordion-title" />
            <div className="accordion-icon">{i === 0 ? `▼` : `▶`}</div>
          </div>
        </div>
      ))}
    </WireframeAccordion>
  );
};

const renderOverlayContent = (width: number, height: number) => (
  <WireframeOverlay style={{ width, height }}>
    <div className="overlay-content">
      <div className="overlay-close">×</div>
    </div>
  </WireframeOverlay>
);

const renderOverlayBgContent = (width: number, height: number) => (
  <div
    style={{ width, height, background: `rgba(0, 0, 0, 0.3)`, borderRadius: 4 }}
  />
);

const renderPopupCardContent = (width: number, height: number) => (
  <div
    style={{
      width,
      height,
      background: `#ffffff`,
      borderRadius: 8,
      boxShadow: `0 4px 20px rgba(0, 0, 0, 0.15)`,
      position: `relative`,
    }}
  >
    <div
      style={{
        position: `absolute`,
        top: 8,
        right: 12,
        color: `#94a3b8`,
        fontSize: 16,
      }}
    >
      ×
    </div>
  </div>
);

const renderDividerContent = (width: number, _height: number) => (
  <WireframeDivider style={{ width }}>
    <div className="divider-line" />
  </WireframeDivider>
);

const renderSimpleTextContent = (width: number, height: number) => (
  <WireframeSimpleText style={{ width, height }}>
    {Array.from({ length: buildTextLineCount(height) }, (_, i) => (
      <div key={i} className="simple-bar" />
    ))}
  </WireframeSimpleText>
);

const renderHeroContent = (width: number, height: number) => (
  <WireframeHero style={{ width, height }}>
    <div className="hero-text">
      <div className="hero-title" />
      <div className="hero-subtitle" />
      <div className="hero-cta" />
    </div>
  </WireframeHero>
);

const renderFormContent = (width: number, height: number) => (
  <WireframeForm style={{ width, height }}>
    <div className="form-field">
      <div className="form-label" />
      <div className="form-input" />
    </div>
    <div className="form-field">
      <div className="form-label" />
      <div className="form-input" />
    </div>
    <div className="form-submit" />
  </WireframeForm>
);

const renderUgcContent = (width: number, height: number) => {
  const gap = 4;
  const cols = 3;
  const cellW = (width - gap * (cols + 1)) / cols;
  const rows = Math.max(1, Math.floor((height - gap) / (cellW + gap)));
  const cells = [];
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      cells.push(
        <div
          key={`${r}-${c}`}
          className="ugc-cell"
          style={{ width: cellW, height: cellW }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#94a3b8"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="2" y="2" width="20" height="20" rx="3" />
            <circle cx="8.5" cy="8.5" r="2.5" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
        </div>,
      );
    }
  }
  return (
    <WireframeUgc style={{ width, height }}>
      <div className="ugc-grid" style={{ gap, padding: gap }}>
        {cells}
      </div>
    </WireframeUgc>
  );
};

const renderSearchContent = (width: number, height: number) => (
  <WireframeSearch style={{ width, height }}>
    <div className="search-icon">⌕</div>
    <div className="search-placeholder" />
  </WireframeSearch>
);

const renderTestimonialsContent = (width: number, height: number) => (
  <WireframeTestimonials style={{ width, height }}>
    <div className="testimonial-avatar" />
    <div className="testimonial-text" />
    <div className="testimonial-stars">★★★★★</div>
    <div className="testimonial-name" />
  </WireframeTestimonials>
);

const renderProductGridContent = (width: number, height: number) => (
  <WireframeProductGrid style={{ width, height }}>
    <div className="grid-item">
      <div className="grid-img" />
      <div className="grid-title" />
      <div className="grid-price" />
    </div>
    <div className="grid-item">
      <div className="grid-img" />
      <div className="grid-title" />
      <div className="grid-price" />
    </div>
    <div className="grid-item">
      <div className="grid-img" />
      <div className="grid-title" />
      <div className="grid-price" />
    </div>
    <div className="grid-item">
      <div className="grid-img" />
      <div className="grid-title" />
      <div className="grid-price" />
    </div>
  </WireframeProductGrid>
);

const renderProductSliderContent = (width: number, height: number) => (
  <WireframeProductSlider style={{ width, height }}>
    <div className="slider-cards">
      {[0, 1, 2].map((i) => (
        <div key={i} className={`slider-card${i === 2 ? ` partial` : ``}`}>
          <div className="slider-card-img">
            <svg
              className="img-icon"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="4"
                y="8"
                width="32"
                height="24"
                rx="3"
                stroke="#94a3b8"
                strokeWidth="1.5"
                fill="none"
              />
              <circle cx="14" cy="17" r="3" fill="#cbd5e1" />
              <path d="M8 28 l8-8 4 4 6-6 8 10z" fill="#cbd5e1" />
            </svg>
          </div>
          <div className="slider-card-info">
            <div className="slider-card-title" />
            <div className="slider-card-price" />
          </div>
        </div>
      ))}
    </div>
    <div className="slider-arrow right">
      <svg
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
      >
        <path
          d="M6 3l5 5-5 5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  </WireframeProductSlider>
);

const IconHeader = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
  >
    <line x1="2" y1="4" x2="14" y2="4" />
    <line x1="2" y1="8" x2="10" y2="8" />
    <line x1="2" y1="12" x2="12" y2="12" />
  </svg>
);

const IconSimpleText = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
  >
    <line x1="3" y1="3" x2="13" y2="3" />
    <line x1="8" y1="3" x2="8" y2="13" />
    <line x1="5" y1="13" x2="11" y2="13" />
  </svg>
);

const IconHero = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1" y="3" width="14" height="10" rx="2" />
    <path d="M1 10l4-3 3 2 4-4 3 3" />
    <circle cx="11" cy="5.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const IconNav = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1" y="4" width="14" height="9" rx="2" />
    <line x1="5.5" y1="4" x2="5.5" y2="13" />
    <line x1="10.5" y1="4" x2="10.5" y2="13" />
  </svg>
);

const IconImage = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1.5" y="2.5" width="13" height="11" rx="2" />
    <circle cx="5" cy="6" r="1.5" />
    <path d="M1.5 11l3.5-3 2.5 2 3-3.5 4 4.5" />
  </svg>
);

const IconTextBlock = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
  >
    <line x1="2" y1="3" x2="14" y2="3" />
    <line x1="2" y1="6.5" x2="12" y2="6.5" />
    <line x1="2" y1="10" x2="14" y2="10" />
    <line x1="2" y1="13" x2="9" y2="13" />
  </svg>
);

const IconButton = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
  >
    <rect x="1.5" y="4" width="13" height="8" rx="3" />
    <line x1="5" y1="8" x2="11" y2="8" />
  </svg>
);

const IconSearch = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
  >
    <circle cx="7" cy="7" r="4.5" />
    <line x1="10.5" y1="10.5" x2="14" y2="14" />
  </svg>
);

const IconForm = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="2" width="12" height="5" rx="1.5" />
    <rect x="2" y="9" width="12" height="5" rx="1.5" />
    <line x1="4" y1="4.5" x2="6" y2="4.5" />
    <line x1="4" y1="11.5" x2="6" y2="11.5" />
  </svg>
);

const IconProductCard = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 1.5L2 4v9.5a1 1 0 001 1h10a1 1 0 001-1V4l-2-2.5H4z" />
    <line x1="2" y1="4" x2="14" y2="4" />
    <path d="M5.5 7a2.5 2.5 0 005 0" />
  </svg>
);

const IconProductGrid = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1.5" y="1.5" width="5" height="5" rx="1" />
    <rect x="9.5" y="1.5" width="5" height="5" rx="1" />
    <rect x="1.5" y="9.5" width="5" height="5" rx="1" />
    <rect x="9.5" y="9.5" width="5" height="5" rx="1" />
  </svg>
);

const IconProductSlider = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3.5" y="2" width="9" height="12" rx="1.5" />
    <polyline points="2,6 0.5,8 2,10" />
    <polyline points="14,6 15.5,8 14,10" />
  </svg>
);

const IconTestimonials = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1.5 2.5h13v8.5h-5.5L5.5 14v-3H1.5z" />
    <path
      d="M5 6.5q0-1.2 1-1.5"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
    <path
      d="M9 6.5q0-1.2 1-1.5"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
  </svg>
);

const IconAccordion = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="2" y1="3" x2="10" y2="3" />
    <polyline points="12,2 14,3 12,4" />
    <line x1="2" y1="8" x2="10" y2="8" />
    <polyline points="12,7 14,8 12,9" />
    <line x1="2" y1="13" x2="10" y2="13" />
    <polyline points="12,12 14,13 12,14" />
  </svg>
);

const IconOverlay = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="0.5" y="1" width="11" height="9" rx="1.5" opacity="0.4" />
    <rect x="4" y="5.5" width="11.5" height="9.5" rx="1.5" />
  </svg>
);

const IconUgc = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1" y="1" width="6" height="6" rx="1" />
    <rect x="9" y="1" width="6" height="6" rx="1" />
    <rect x="1" y="9" width="6" height="6" rx="1" />
    <rect x="9" y="9" width="6" height="6" rx="1" />
  </svg>
);

const IconDivider = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
  >
    <line x1="1" y1="8" x2="15" y2="8" />
  </svg>
);

const IconUploadImage = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2.5 10.5v2a1.5 1.5 0 001.5 1.5h8a1.5 1.5 0 001.5-1.5v-2" />
    <polyline points="5,6 8,2.5 11,6" />
    <line x1="8" y1="2.5" x2="8" y2="10.5" />
  </svg>
);

const IconFreeText = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
  >
    <line x1="8" y1="2" x2="8" y2="14" />
    <line x1="5" y1="2" x2="11" y2="2" />
    <line x1="6" y1="14" x2="10" y2="14" />
  </svg>
);

const IconSketch = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11.5 1.5l3 3-9 9H2.5v-3z" />
    <line x1="9.5" y1="3.5" x2="12.5" y2="6.5" />
  </svg>
);

const renderUploadImageContent = (
  width: number,
  height: number,
  imageDataUrl?: string,
) => (
  <WireframeUploadImage style={{ width, height }}>
    {imageDataUrl ? (
      <img
        src={imageDataUrl}
        alt=""
        style={{ width: `100%`, height: `100%`, objectFit: `cover` }}
      />
    ) : (
      <div className="upload-placeholder">
        <IconUploadImage />
        <span className="upload-label">Upload Image</span>
      </div>
    )}
  </WireframeUploadImage>
);

const renderFreeTextContent = (
  width: number,
  height: number,
  text?: string,
  textColor?: string,
) => (
  <WireframeFreeText style={{ width, height, color: textColor || `#1c1c1e` }}>
    {text || <span className="placeholder-text">Double-click to edit</span>}
  </WireframeFreeText>
);

const IconVideo = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1.5" y="3" width="10" height="10" rx="2" />
    <path d="M11.5 6.5L15 4.5v7l-3.5-2" />
  </svg>
);

const renderVideoContent = (width: number, height: number) => (
  <WireframeVideo style={{ width, height }}>
    <svg
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="play-icon"
    >
      <circle
        cx="24"
        cy="24"
        r="18"
        stroke="#94a3b8"
        strokeWidth="2"
        fill="rgba(148, 163, 184, 0.1)"
      />
      <path d="M20 16l12 8-12 8z" fill="#94a3b8" />
    </svg>
  </WireframeVideo>
);

const BLOCK_DEFINITIONS: BlockDefinition[] = [
  {
    type: `header`,
    label: `Header`,
    defaultWidth: 280,
    defaultHeight: 36,
    fillColor: `#FFFFFF`,
    strokeColor: `transparent`,
    icon: <IconHeader />,
    renderContent: renderHeaderContent,
  },
  {
    type: `simpleText`,
    label: `Simple Text`,
    defaultWidth: 200,
    defaultHeight: 32,
    fillColor: `#FFFFFF`,
    strokeColor: `transparent`,
    icon: <IconSimpleText />,
    renderContent: renderSimpleTextContent,
  },
  {
    type: `hero`,
    label: `Hero Banner`,
    defaultWidth: 320,
    defaultHeight: 160,
    fillColor: `#F1F5F9`,
    strokeColor: `transparent`,
    icon: <IconHero />,
    renderContent: renderHeroContent,
  },
  {
    type: `nav`,
    label: `Navigation`,
    defaultWidth: 280,
    defaultHeight: 44,
    fillColor: `#FFFFFF`,
    strokeColor: `transparent`,
    icon: <IconNav />,
    renderContent: renderNavContent,
  },
  {
    type: `image`,
    label: `Image`,
    defaultWidth: 200,
    defaultHeight: 150,
    fillColor: `#F8FAFC`,
    strokeColor: `transparent`,
    icon: <IconImage />,
    renderContent: renderImageContent,
  },
  {
    type: `paragraph`,
    label: `Text Block`,
    defaultWidth: 240,
    defaultHeight: 80,
    fillColor: `#FFFFFF`,
    strokeColor: `transparent`,
    icon: <IconTextBlock />,
    renderContent: renderTextContent,
  },
  {
    type: `button`,
    label: `Button`,
    defaultWidth: 120,
    defaultHeight: 40,
    fillColor: `#3B82F6`,
    strokeColor: `#2563EB`,
    icon: <IconButton />,
    renderContent: renderButtonContent,
  },
  {
    type: `search`,
    label: `Search Bar`,
    defaultWidth: 260,
    defaultHeight: 40,
    fillColor: `#FFFFFF`,
    strokeColor: `transparent`,
    icon: <IconSearch />,
    renderContent: renderSearchContent,
  },
  {
    type: `form`,
    label: `Form`,
    defaultWidth: 240,
    defaultHeight: 160,
    fillColor: `#FFFFFF`,
    strokeColor: `transparent`,
    icon: <IconForm />,
    renderContent: renderFormContent,
  },
  {
    type: `product`,
    label: `Product Card`,
    defaultWidth: 180,
    defaultHeight: 220,
    fillColor: `#FFFFFF`,
    strokeColor: `transparent`,
    icon: <IconProductCard />,
    renderContent: renderProductContent,
  },
  {
    type: `productGrid`,
    label: `Product Grid`,
    defaultWidth: 280,
    defaultHeight: 260,
    fillColor: `#FFFFFF`,
    strokeColor: `transparent`,
    icon: <IconProductGrid />,
    renderContent: renderProductGridContent,
  },
  {
    type: `productSlider`,
    label: `Product Slider`,
    defaultWidth: 320,
    defaultHeight: 140,
    fillColor: `#FFFFFF`,
    strokeColor: `transparent`,
    icon: <IconProductSlider />,
    renderContent: renderProductSliderContent,
  },
  {
    type: `testimonials`,
    label: `Testimonials`,
    defaultWidth: 220,
    defaultHeight: 140,
    fillColor: `#FFFFFF`,
    strokeColor: `transparent`,
    icon: <IconTestimonials />,
    renderContent: renderTestimonialsContent,
  },
  {
    type: `accordion`,
    label: `Accordion`,
    defaultWidth: 260,
    defaultHeight: 90,
    fillColor: `#FFFFFF`,
    strokeColor: `transparent`,
    icon: <IconAccordion />,
    renderContent: renderAccordionContent,
  },
  {
    type: `overlay`,
    label: `Modal/Popup`,
    defaultWidth: 280,
    defaultHeight: 180,
    fillColor: `#FFFFFF`,
    strokeColor: `#94A3B8`,
    icon: <IconOverlay />,
    renderContent: renderOverlayContent,
  },
  {
    type: `ugc`,
    label: `UGC`,
    defaultWidth: 240,
    defaultHeight: 160,
    fillColor: `#F8FAFC`,
    strokeColor: `transparent`,
    icon: <IconUgc />,
    renderContent: renderUgcContent,
  },
  {
    type: `divider`,
    label: `Divider`,
    defaultWidth: 200,
    defaultHeight: 20,
    fillColor: `transparent`,
    strokeColor: `transparent`,
    icon: <IconDivider />,
    renderContent: renderDividerContent,
  },
  {
    type: `uploadImage`,
    label: `Upload Image`,
    defaultWidth: 200,
    defaultHeight: 150,
    fillColor: `#F8FAFC`,
    strokeColor: `transparent`,
    icon: <IconUploadImage />,
    renderContent: (w, h) => renderUploadImageContent(w, h),
  },
  {
    type: `freeText`,
    label: `Free Text`,
    defaultWidth: 200,
    defaultHeight: 60,
    fillColor: `transparent`,
    strokeColor: `transparent`,
    icon: <IconFreeText />,
    renderContent: (w, h) => renderFreeTextContent(w, h),
  },
  {
    type: `video`,
    label: `Video`,
    defaultWidth: 280,
    defaultHeight: 160,
    fillColor: `#0F172A`,
    strokeColor: `#334155`,
    icon: <IconVideo />,
    renderContent: renderVideoContent,
  },
  {
    type: `group`,
    label: `Group`,
    defaultWidth: 200,
    defaultHeight: 200,
    fillColor: `transparent`,
    strokeColor: `transparent`,
    icon: <span />,
    renderContent: () => null,
  },
  {
    type: `overlayBg`,
    label: `Overlay BG`,
    defaultWidth: 280,
    defaultHeight: 180,
    fillColor: `rgba(0, 0, 0, 0.3)`,
    strokeColor: `transparent`,
    icon: <span />,
    renderContent: renderOverlayBgContent,
  },
  {
    type: `popupCard`,
    label: `Popup Card`,
    defaultWidth: 238,
    defaultHeight: 144,
    fillColor: `#FFFFFF`,
    strokeColor: `transparent`,
    icon: <span />,
    renderContent: renderPopupCardContent,
  },
];

const getBlockDefinition = (type: string): BlockDefinition =>
  BLOCK_DEFINITIONS.find((b) => b.type === type) ?? BLOCK_DEFINITIONS[0];

const BLOCKS_STRIP_ORDER = [
  `header`,
  `nav`,
  `hero`,
  `uploadImage`,
  `freeText`,
  `video`,
  `image`,
  `paragraph`,
  `button`,
  `search`,
  `form`,
  `product`,
  `productGrid`,
  `productSlider`,
  `testimonials`,
  `accordion`,
  `overlay`,
  `ugc`,
  `divider`,
];

const TEXT_EDITABLE_ELEMENT_TYPES = new Set([`header`, `button`, `freeText`]);

const isTextEditableType = (type: string) =>
  TEXT_EDITABLE_ELEMENT_TYPES.has(type);

interface UngroupChild {
  type: string;
  xOffset: number;
  yOffset: number;
  width: number;
  height: number;
  useRatio?: boolean;
}

const UNGROUP_MAP: Record<string, UngroupChild[]> = {
  hero: [
    { type: `header`, xOffset: 0, yOffset: 0, width: 320, height: 36 },
    { type: `paragraph`, xOffset: 40, yOffset: 44, width: 240, height: 44 },
    { type: `button`, xOffset: 120, yOffset: 96, width: 80, height: 28 },
  ],
  form: [
    { type: `paragraph`, xOffset: 12, yOffset: 12, width: 60, height: 12 },
    { type: `simpleText`, xOffset: 12, yOffset: 26, width: 216, height: 28 },
    { type: `paragraph`, xOffset: 12, yOffset: 60, width: 60, height: 12 },
    { type: `simpleText`, xOffset: 12, yOffset: 74, width: 216, height: 28 },
    { type: `button`, xOffset: 12, yOffset: 118, width: 216, height: 28 },
  ],
  product: [
    { type: `image`, xOffset: 8, yOffset: 8, width: 164, height: 113 },
    { type: `paragraph`, xOffset: 12, yOffset: 129, width: 108, height: 14 },
    { type: `simpleText`, xOffset: 12, yOffset: 149, width: 63, height: 14 },
    { type: `button`, xOffset: 12, yOffset: 174, width: 156, height: 22 },
  ],
  testimonials: [
    { type: `image`, xOffset: 82, yOffset: 10, width: 28, height: 28 },
    { type: `paragraph`, xOffset: 20, yOffset: 50, width: 176, height: 24 },
    { type: `simpleText`, xOffset: 60, yOffset: 86, width: 80, height: 14 },
    { type: `simpleText`, xOffset: 60, yOffset: 106, width: 50, height: 10 },
  ],
  overlay: [
    {
      type: `overlayBg`,
      xOffset: 0,
      yOffset: 0,
      width: 1,
      height: 1,
      useRatio: true,
    },
    {
      type: `popupCard`,
      xOffset: 0.075,
      yOffset: 0.1,
      width: 0.85,
      height: 0.8,
      useRatio: true,
    },
  ],
};

const canUngroup = (type: string) => type in UNGROUP_MAP || type === `group`;

interface CanvasElement {
  id: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  visible: boolean;
  locked: boolean;
  name: string;
  customText?: string;
  imageDataUrl?: string;
  textColor?: string;
  sketchPoints?: { x: number; y: number }[][];
  groupChildren?: CanvasElement[];
}

type ResizeHandle = `nw` | `n` | `ne` | `e` | `se` | `s` | `sw` | `w` | null;

const CANVAS_WIDTH_MOBILE = 440;
const CANVAS_HEIGHT_MOBILE = 540;
const CANVAS_WIDTH_DESKTOP = 500;
const CANVAS_HEIGHT_DESKTOP = 400;
const MIN_SIZE = 20;
const MIN_CANVAS_SIZE = 200;
const SNAP_THRESHOLD = 6;
const SNAP_GUIDE_COLOR = `#ff3b30`;
const ARROW_KEYS = [`ArrowUp`, `ArrowDown`, `ArrowLeft`, `ArrowRight`];

interface ContextMenuState {
  x: number;
  y: number;
  elementId: string;
}

const findTopVisibleUnlockedElementAtPosition = (
  elements: CanvasElement[],
  x: number,
  y: number,
): CanvasElement | undefined =>
  [...elements]
    .reverse()
    .find(
      (el) =>
        el.visible &&
        !el.locked &&
        x >= el.x &&
        x <= el.x + el.width &&
        y >= el.y &&
        y <= el.y + el.height,
    );

interface MarqueeBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

const getMarqueeBounds = (m: {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}): MarqueeBounds => ({
  left: Math.min(m.startX, m.endX),
  top: Math.min(m.startY, m.endY),
  width: Math.abs(m.endX - m.startX),
  height: Math.abs(m.endY - m.startY),
});

const getGroupChildrenBounds = (children: CanvasElement[]) => ({
  originalWidth:
    children.reduce((max, c) => Math.max(max, c.x + c.width), 0) || 1,
  originalHeight:
    children.reduce((max, c) => Math.max(max, c.y + c.height), 0) || 1,
});

const renderGroupContent = (group: CanvasElement) => {
  if (!group.groupChildren) return null;
  const { originalWidth, originalHeight } = getGroupChildrenBounds(
    group.groupChildren,
  );
  const scaleX = group.width / originalWidth;
  const scaleY = group.height / originalHeight;
  return (
    <div
      style={{
        width: group.width,
        height: group.height,
        position: `relative`,
        overflow: `hidden`,
      }}
    >
      {group.groupChildren.map((child, childIndex) => {
        const def = getBlockDefinition(child.type);
        const scaledWidth = Math.round(child.width * scaleX);
        const scaledHeight = Math.round(child.height * scaleY);
        return (
          <div
            key={childIndex}
            style={{
              position: `absolute`,
              left: Math.round(child.x * scaleX),
              top: Math.round(child.y * scaleY),
              width: scaledWidth,
              height: scaledHeight,
              pointerEvents: `none`,
            }}
          >
            {renderElementContent(
              { ...child, width: scaledWidth, height: scaledHeight },
              def,
            )}
          </div>
        );
      })}
    </div>
  );
};

const renderElementContent = (
  element: CanvasElement,
  definition: BlockDefinition,
) => {
  switch (element.type) {
    case `simpleText`:
      return renderSimpleTextContent(element.width, element.height);
    case `paragraph`:
      return renderTextContent(element.width, element.height);
    case `button`:
      return renderButtonContent(
        element.width,
        element.height,
        element.customText,
      );
    case `header`:
      return renderHeaderContent(
        element.width,
        element.height,
        element.customText,
      );
    case `uploadImage`:
      return renderUploadImageContent(
        element.width,
        element.height,
        element.imageDataUrl,
      );
    case `freeText`:
      return renderFreeTextContent(
        element.width,
        element.height,
        element.customText,
        element.textColor,
      );
    case `video`:
      return renderVideoContent(element.width, element.height);
    case `overlayBg`:
      return renderOverlayBgContent(element.width, element.height);
    case `popupCard`:
      return renderPopupCardContent(element.width, element.height);
    case `sketch`:
      return (
        <svg
          width={element.width}
          height={element.height}
          style={{
            position: `absolute`,
            top: 0,
            left: 0,
            pointerEvents: `none`,
          }}
        >
          {(element.sketchPoints || []).map((stroke, si) => (
            <polyline
              key={si}
              points={stroke.map((p) => `${p.x},${p.y}`).join(` `)}
              fill="none"
              stroke="#1c1c1e"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
        </svg>
      );
    case `group`:
      return renderGroupContent(element);
    default:
      return definition.renderContent(element.width, element.height);
  }
};

function PanelBlockItem({
  definition,
  onAdd,
}: {
  definition: BlockDefinition;
  onAdd: (type: string) => void;
}) {
  return (
    <PanelBlock
      onClick={() => onAdd(definition.type)}
      $strokeColor={definition.strokeColor}
    >
      <span className="icon">{definition.icon}</span>
      <span className="label">{definition.label}</span>
    </PanelBlock>
  );
}

function LayerItem({
  element,
  isSelected,
  onSelect,
  onToggleVisibility,
  onToggleLock,
  onDelete,
  onDuplicate,
  onSendToBack,
}: {
  element: CanvasElement;
  isSelected: boolean;
  onSelect: () => void;
  onToggleVisibility: () => void;
  onToggleLock: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onSendToBack: () => void;
}) {
  const definition = getBlockDefinition(element.type);

  return (
    <LayerRow $isSelected={isSelected} onClick={onSelect}>
      <span className="layer-icon">{definition.icon}</span>
      <span className="layer-name">{element.name}</span>
      <div className="layer-actions">
        <button
          type="button"
          className="layer-action-btn"
          onClick={(e) => {
            e.stopPropagation();
            onToggleVisibility();
          }}
          title={element.visible ? `Hide` : `Show`}
        >
          {element.visible ? (
            <IoEyeOutline size={14} />
          ) : (
            <IoEyeOffOutline size={14} />
          )}
        </button>
        <button
          type="button"
          className="layer-action-btn"
          onClick={(e) => {
            e.stopPropagation();
            onToggleLock();
          }}
          title={element.locked ? `Unlock` : `Lock`}
        >
          {element.locked ? (
            <IoLockClosedOutline size={14} />
          ) : (
            <IoLockOpenOutline size={14} />
          )}
        </button>
        <button
          type="button"
          className="layer-action-btn"
          onClick={(e) => {
            e.stopPropagation();
            onSendToBack();
          }}
          title="Send to back"
        >
          <IoUnlinkOutline size={14} />
        </button>
        <button
          type="button"
          className="layer-action-btn"
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate();
          }}
          title="Duplicate"
        >
          <IoCopyOutline size={14} />
        </button>
        <button
          type="button"
          className="layer-action-btn delete"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          title="Delete"
        >
          <IoTrashOutline size={14} />
        </button>
      </div>
    </LayerRow>
  );
}

const drawRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.arcTo(x + width, y, x + width, y + radius, radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.arcTo(x + width, y + height, x + width - radius, y + height, radius);
  ctx.lineTo(x + radius, y + height);
  ctx.arcTo(x, y + height, x, y + height - radius, radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y, x + radius, y, radius);
  ctx.closePath();
};

const drawWrappedText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  startX: number,
  startY: number,
  maxWidth: number,
  lineHeight: number,
) => {
  const words = text.split(` `);
  let line = ``;
  let currentY = startY;
  words.forEach((word) => {
    const testLine = line ? `${line} ${word}` : word;
    if (ctx.measureText(testLine).width > maxWidth && line) {
      ctx.fillText(line, startX, currentY);
      line = word;
      currentY += lineHeight;
    } else {
      line = testLine;
    }
  });
  if (line) ctx.fillText(line, startX, currentY);
};

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const drawWireframeElement = async (
  ctx: CanvasRenderingContext2D,
  element: CanvasElement,
) => {
  const { x, y, width, height, type } = element;
  const definition = getBlockDefinition(type);

  ctx.save();

  switch (type) {
    case `header`:
      ctx.fillStyle = `#FFFFFF`;
      ctx.strokeStyle = `#E2E8F0`;
      ctx.lineWidth = 1;
      drawRoundedRect(ctx, x, y, width, height, 4);
      ctx.fill();
      ctx.stroke();

      if (element.customText) {
        ctx.fillStyle = `#334155`;
        ctx.font = `bold 14px Inter, system-ui, sans-serif`;
        ctx.textAlign = `center`;
        ctx.textBaseline = `middle`;
        ctx.fillText(element.customText, x + width / 2, y + height / 2);
      } else {
        ctx.fillStyle = `#CBD5E1`;
        ctx.fillRect(x + width / 2 - 40, y + height / 2 - 5, 80, 10);
      }
      break;

    case `simpleText`: {
      ctx.fillStyle = `#FFFFFF`;
      ctx.strokeStyle = `#E2E8F0`;
      ctx.lineWidth = 1;
      drawRoundedRect(ctx, x, y, width, height, 4);
      ctx.fill();
      ctx.stroke();

      const stAvailableH = height - TEXT_PADDING_Y * 2;
      const stLineCount = Math.max(
        1,
        Math.floor(stAvailableH / (TEXT_LINE_HEIGHT + TEXT_LINE_GAP)),
      );
      const stBarWidth = width - 20;
      ctx.fillStyle = `#CBD5E1`;
      for (let i = 0; i < stLineCount; i += 1) {
        const isLast = i === stLineCount - 1 && stLineCount > 1;
        const bw = isLast ? stBarWidth * 0.6 : stBarWidth * 0.9;
        drawRoundedRect(
          ctx,
          x + 10,
          y + TEXT_PADDING_Y + i * (TEXT_LINE_HEIGHT + TEXT_LINE_GAP),
          bw,
          TEXT_LINE_HEIGHT,
          2,
        );
        ctx.fill();
      }
      break;
    }

    case `nav`:
      ctx.fillStyle = `#FFFFFF`;
      ctx.strokeStyle = `#E2E8F0`;
      ctx.lineWidth = 1;
      drawRoundedRect(ctx, x, y, width, height, 4);
      ctx.fill();
      ctx.stroke();

      // eslint-disable-next-line no-case-declarations
      const tabWidth = width / 4 - 8;
      for (let i = 0; i < 4; i += 1) {
        ctx.fillStyle = i === 0 ? `#3B82F6` : `#E2E8F0`;
        drawRoundedRect(
          ctx,
          x + 4 + i * (tabWidth + 8),
          y + 8,
          tabWidth,
          height - 16,
          4,
        );
        ctx.fill();
      }
      break;

    case `image`:
      ctx.fillStyle = `#F1F5F9`;
      ctx.strokeStyle = `#CBD5E1`;
      ctx.lineWidth = 1;
      drawRoundedRect(ctx, x, y, width, height, 4);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = `#94A3B8`;
      ctx.font = `24px sans-serif`;
      ctx.textAlign = `center`;
      ctx.textBaseline = `middle`;
      ctx.fillText(`🖼`, x + width / 2, y + height / 2);
      break;

    case `paragraph`:
      ctx.fillStyle = `#FFFFFF`;
      ctx.strokeStyle = `#E2E8F0`;
      ctx.lineWidth = 1;
      drawRoundedRect(ctx, x, y, width, height, 4);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = `#CBD5E1`;
      {
        const paddingX = 14;
        const availableH = height - TEXT_PADDING_Y * 2;
        const lineCount = Math.max(
          1,
          Math.floor(availableH / (TEXT_LINE_HEIGHT + TEXT_LINE_GAP)),
        );
        const barWidth = width - paddingX * 2;
        for (let i = 0; i < lineCount; i += 1) {
          const isLast = i === lineCount - 1;
          const w = isLast
            ? barWidth * TEXT_BAR_LAST_WIDTH_RATIO
            : barWidth * TEXT_BAR_WIDTH_RATIO;
          drawRoundedRect(
            ctx,
            x + paddingX,
            y + TEXT_PADDING_Y + i * (TEXT_LINE_HEIGHT + TEXT_LINE_GAP),
            w,
            TEXT_LINE_HEIGHT,
            2,
          );
          ctx.fill();
        }
      }
      break;

    case `button`:
      ctx.fillStyle = `#3B82F6`;
      drawRoundedRect(ctx, x, y, width, height, 6);
      ctx.fill();

      ctx.fillStyle = `#FFFFFF`;
      ctx.font = `bold 12px Inter, system-ui, sans-serif`;
      ctx.textAlign = `center`;
      ctx.textBaseline = `middle`;
      ctx.fillText(
        element.customText || `Button`,
        x + width / 2,
        y + height / 2,
      );
      break;

    case `product`:
      ctx.fillStyle = `#FFFFFF`;
      ctx.strokeStyle = `#E2E8F0`;
      ctx.lineWidth = 1;
      drawRoundedRect(ctx, x, y, width, height, 6);
      ctx.fill();
      ctx.stroke();

      // eslint-disable-next-line no-case-declarations
      const imageHeight = height * 0.55;
      ctx.fillStyle = `#F1F5F9`;
      drawRoundedRect(ctx, x + 8, y + 8, width - 16, imageHeight - 8, 4);
      ctx.fill();

      ctx.fillStyle = `#94A3B8`;
      ctx.font = `20px sans-serif`;
      ctx.textAlign = `center`;
      ctx.textBaseline = `middle`;
      ctx.fillText(`🖼`, x + width / 2, y + imageHeight / 2);

      ctx.fillStyle = `#CBD5E1`;
      ctx.fillRect(x + 12, y + imageHeight + 8, width * 0.6, 10);

      ctx.fillStyle = `#64748B`;
      ctx.fillRect(x + 12, y + imageHeight + 24, width * 0.35, 10);

      ctx.fillStyle = `#3B82F6`;
      drawRoundedRect(ctx, x + 12, y + height - 32, width - 24, 22, 4);
      ctx.fill();
      break;

    case `accordion`:
      ctx.fillStyle = `#FFFFFF`;
      ctx.strokeStyle = `#E2E8F0`;
      ctx.lineWidth = 1;
      drawRoundedRect(ctx, x, y, width, height, 4);
      ctx.fill();
      ctx.stroke();

      // eslint-disable-next-line no-case-declarations
      const accItemCount = Math.max(
        2,
        Math.floor(height / ACCORDION_ITEM_HEIGHT),
      );
      // eslint-disable-next-line no-case-declarations
      const accItemHeight = height / accItemCount;
      ctx.strokeStyle = `#E2E8F0`;
      for (let ai = 1; ai < accItemCount; ai += 1) {
        ctx.beginPath();
        ctx.moveTo(x, y + accItemHeight * ai);
        ctx.lineTo(x + width, y + accItemHeight * ai);
        ctx.stroke();
      }

      ctx.fillStyle = `#CBD5E1`;
      for (let ai = 0; ai < accItemCount; ai += 1) {
        ctx.fillRect(
          x + 12,
          y + accItemHeight * ai + accItemHeight / 2 - 5,
          width * 0.9 - 40,
          10,
        );
      }

      ctx.fillStyle = `#94A3B8`;
      ctx.font = `12px sans-serif`;
      ctx.textAlign = `center`;
      ctx.textBaseline = `middle`;
      for (let ai = 0; ai < accItemCount; ai += 1) {
        ctx.fillText(
          ai === 0 ? `▼` : `▶`,
          x + width - 20,
          y + accItemHeight * ai + accItemHeight / 2,
        );
      }
      break;

    case `overlay`:
      ctx.fillStyle = `rgba(0, 0, 0, 0.3)`;
      ctx.fillRect(x, y, width, height);

      // eslint-disable-next-line no-case-declarations
      const modalWidth = width * 0.85;
      // eslint-disable-next-line no-case-declarations
      const modalHeight = height * 0.8;
      // eslint-disable-next-line no-case-declarations
      const modalX = x + (width - modalWidth) / 2;
      // eslint-disable-next-line no-case-declarations
      const modalY = y + (height - modalHeight) / 2;

      ctx.fillStyle = `#FFFFFF`;
      ctx.shadowColor = `rgba(0, 0, 0, 0.15)`;
      ctx.shadowBlur = 20;
      ctx.shadowOffsetY = 4;
      drawRoundedRect(ctx, modalX, modalY, modalWidth, modalHeight, 8);
      ctx.fill();
      ctx.shadowColor = `transparent`;

      ctx.fillStyle = `#94A3B8`;
      ctx.font = `16px sans-serif`;
      ctx.textAlign = `right`;
      ctx.fillText(`×`, modalX + modalWidth - 12, modalY + 20);
      break;

    case `overlayBg`:
      ctx.fillStyle = `rgba(0, 0, 0, 0.3)`;
      ctx.fillRect(x, y, width, height);
      break;

    case `popupCard`:
      ctx.fillStyle = `#FFFFFF`;
      ctx.shadowColor = `rgba(0, 0, 0, 0.15)`;
      ctx.shadowBlur = 20;
      ctx.shadowOffsetY = 4;
      drawRoundedRect(ctx, x, y, width, height, 8);
      ctx.fill();
      ctx.shadowColor = `transparent`;
      ctx.fillStyle = `#94A3B8`;
      ctx.font = `16px sans-serif`;
      ctx.textAlign = `right`;
      ctx.textBaseline = `top`;
      ctx.fillText(`×`, x + width - 12, y + 8);
      break;

    case `divider`:
      ctx.strokeStyle = `#CBD5E1`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, y + height / 2);
      ctx.lineTo(x + width, y + height / 2);
      ctx.stroke();
      break;

    case `hero`:
      ctx.fillStyle = `#F1F5F9`;
      ctx.strokeStyle = `#CBD5E1`;
      ctx.lineWidth = 1;
      drawRoundedRect(ctx, x, y, width, height, 6);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = `#CBD5E1`;
      ctx.fillRect(x + width * 0.15, y + height * 0.2, width * 0.7, 14);
      ctx.fillStyle = `#94A3B8`;
      ctx.fillRect(x + width * 0.25, y + height * 0.2 + 22, width * 0.5, 10);
      ctx.fillStyle = `#3B82F6`;
      drawRoundedRect(ctx, x + width / 2 - 40, y + height * 0.6, 80, 28, 4);
      ctx.fill();
      break;

    case `form`:
      ctx.fillStyle = `#FFFFFF`;
      ctx.strokeStyle = `#E2E8F0`;
      ctx.lineWidth = 1;
      drawRoundedRect(ctx, x, y, width, height, 4);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = `#94A3B8`;
      ctx.fillRect(x + 12, y + 12, width * 0.25, 8);
      ctx.fillStyle = `#F1F5F9`;
      ctx.strokeStyle = `#CBD5E1`;
      drawRoundedRect(ctx, x + 12, y + 26, width - 24, 24, 4);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = `#94A3B8`;
      ctx.fillRect(x + 12, y + 60, width * 0.25, 8);
      ctx.fillStyle = `#F1F5F9`;
      drawRoundedRect(ctx, x + 12, y + 74, width - 24, 24, 4);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = `#3B82F6`;
      drawRoundedRect(ctx, x + 12, y + height - 38, width - 24, 28, 4);
      ctx.fill();
      break;

    case `ugc`: {
      ctx.fillStyle = `#F8FAFC`;
      drawRoundedRect(ctx, x, y, width, height, 4);
      ctx.fill();
      const ugcGap = 4;
      const ugcCols = 3;
      const ugcCellW = (width - ugcGap * (ugcCols + 1)) / ugcCols;
      const ugcRows = Math.max(
        1,
        Math.floor((height - ugcGap) / (ugcCellW + ugcGap)),
      );
      ctx.fillStyle = `#E2E8F0`;
      for (let r = 0; r < ugcRows; r += 1) {
        for (let c = 0; c < ugcCols; c += 1) {
          const cx = x + ugcGap + c * (ugcCellW + ugcGap);
          const cy = y + ugcGap + r * (ugcCellW + ugcGap);
          drawRoundedRect(ctx, cx, cy, ugcCellW, ugcCellW, 3);
          ctx.fill();
        }
      }
      break;
    }

    case `search`:
      ctx.fillStyle = `#FFFFFF`;
      ctx.strokeStyle = `#E2E8F0`;
      ctx.lineWidth = 1;
      drawRoundedRect(ctx, x, y, width, height, height / 2);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = `#94A3B8`;
      ctx.font = `14px sans-serif`;
      ctx.textAlign = `left`;
      ctx.textBaseline = `middle`;
      ctx.fillText(`⌕`, x + 14, y + height / 2);
      ctx.fillStyle = `#CBD5E1`;
      ctx.fillRect(x + 34, y + height / 2 - 4, width * 0.4, 8);
      break;

    case `testimonials`:
      ctx.fillStyle = `#FFFFFF`;
      ctx.strokeStyle = `#E2E8F0`;
      ctx.lineWidth = 1;
      drawRoundedRect(ctx, x, y, width, height, 6);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = `#CBD5E1`;
      ctx.beginPath();
      ctx.arc(x + width / 2, y + 24, 14, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `#E2E8F0`;
      ctx.fillRect(x + 20, y + 50, width - 40, 8);
      ctx.fillRect(x + 30, y + 64, width - 60, 8);
      ctx.fillStyle = `#F59E0B`;
      ctx.font = `12px sans-serif`;
      ctx.textAlign = `center`;
      ctx.fillText(`★★★★★`, x + width / 2, y + 92);
      ctx.fillStyle = `#94A3B8`;
      ctx.fillRect(x + width / 2 - 25, y + 102, 50, 8);
      break;

    case `productGrid`: {
      ctx.fillStyle = `#FFFFFF`;
      ctx.strokeStyle = `#E2E8F0`;
      ctx.lineWidth = 1;
      drawRoundedRect(ctx, x, y, width, height, 4);
      ctx.fill();
      ctx.stroke();
      const cols = 2;
      const rows = 2;
      const gapPG = 8;
      const padPG = 8;
      const cellW = (width - padPG * 2 - gapPG * (cols - 1)) / cols;
      const cellH = (height - padPG * 2 - gapPG * (rows - 1)) / rows;
      for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
          const cx = x + padPG + c * (cellW + gapPG);
          const cy = y + padPG + r * (cellH + gapPG);
          ctx.fillStyle = `#F1F5F9`;
          drawRoundedRect(ctx, cx, cy, cellW, cellH * 0.6, 4);
          ctx.fill();
          ctx.fillStyle = `#CBD5E1`;
          ctx.fillRect(cx + 4, cy + cellH * 0.65, cellW * 0.6, 6);
          ctx.fillStyle = `#64748B`;
          ctx.fillRect(cx + 4, cy + cellH * 0.65 + 10, cellW * 0.35, 6);
        }
      }
      break;
    }

    case `productSlider`: {
      ctx.fillStyle = `#FFFFFF`;
      ctx.strokeStyle = `#E2E8F0`;
      ctx.lineWidth = 1;
      drawRoundedRect(ctx, x, y, width, height, 4);
      ctx.fill();
      ctx.stroke();
      const pad = 10;
      const cardW = (width - pad * 2 - 10 * 2) * 0.4;
      const halfCardW = (width - pad * 2 - 10 * 2) * 0.2;
      const cards = [
        { cx: x + pad, cw: cardW, alpha: 1 },
        { cx: x + pad + cardW + 10, cw: cardW, alpha: 1 },
        { cx: x + pad + (cardW + 10) * 2, cw: halfCardW, alpha: 0.6 },
      ];

      // eslint-disable-next-line no-restricted-syntax
      for (const card of cards) {
        const cardH = height - pad * 2;
        const imgH = cardH * 0.65;
        ctx.globalAlpha = card.alpha;
        ctx.fillStyle = `#FFFFFF`;
        ctx.strokeStyle = `#E2E8F0`;
        ctx.lineWidth = 1;
        drawRoundedRect(ctx, card.cx, y + pad, card.cw, cardH, 8);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = `#F8FAFC`;
        drawRoundedRect(ctx, card.cx, y + pad, card.cw, imgH, 8);
        ctx.fill();
        ctx.strokeStyle = `#F1F5F9`;
        ctx.stroke();
        const iconSize = Math.min(card.cw, imgH) * 0.28;
        const iconX = card.cx + (card.cw - iconSize) / 2;
        const iconY = y + pad + (imgH - iconSize) / 2;
        ctx.strokeStyle = `#94A3B8`;
        ctx.lineWidth = 1.2;
        ctx.globalAlpha = card.alpha * 0.5;
        drawRoundedRect(
          ctx,
          iconX,
          iconY + iconSize * 0.15,
          iconSize,
          iconSize * 0.6,
          2,
        );
        ctx.stroke();
        ctx.fillStyle = `#CBD5E1`;
        ctx.beginPath();
        ctx.arc(
          iconX + iconSize * 0.3,
          iconY + iconSize * 0.35,
          iconSize * 0.1,
          0,
          Math.PI * 2,
        );
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(iconX + iconSize * 0.1, iconY + iconSize * 0.65);
        ctx.lineTo(iconX + iconSize * 0.35, iconY + iconSize * 0.4);
        ctx.lineTo(iconX + iconSize * 0.5, iconY + iconSize * 0.5);
        ctx.lineTo(iconX + iconSize * 0.7, iconY + iconSize * 0.3);
        ctx.lineTo(iconX + iconSize * 0.9, iconY + iconSize * 0.65);
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = card.alpha;
        const infoY = y + pad + imgH + 8;
        ctx.fillStyle = `#CBD5E1`;
        drawRoundedRect(ctx, card.cx + 10, infoY, card.cw * 0.7, 8, 3);
        ctx.fill();
        ctx.fillStyle = `#64748B`;
        drawRoundedRect(ctx, card.cx + 10, infoY + 16, card.cw * 0.35, 10, 3);
        ctx.fill();
        ctx.globalAlpha = 1;
      }
      ctx.fillStyle = `#475569`;
      ctx.font = `bold 14px sans-serif`;
      ctx.textAlign = `center`;
      ctx.textBaseline = `middle`;
      ctx.fillText(`\u203A`, x + width - 16, y + height / 2);
      break;
    }

    case `uploadImage`:
      if (element.imageDataUrl) {
        try {
          const img = await loadImage(element.imageDataUrl);
          ctx.drawImage(img, x, y, width, height);
        } catch (_e) {
          ctx.fillStyle = `#F1F5F9`;
          drawRoundedRect(ctx, x, y, width, height, 4);
          ctx.fill();
        }
      } else {
        ctx.fillStyle = `#F1F5F9`;
        ctx.strokeStyle = `#CBD5E1`;
        ctx.lineWidth = 1;
        drawRoundedRect(ctx, x, y, width, height, 4);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = `#94A3B8`;
        ctx.font = `12px Inter, system-ui, sans-serif`;
        ctx.textAlign = `center`;
        ctx.textBaseline = `middle`;
        ctx.fillText(`Upload Image`, x + width / 2, y + height / 2);
      }
      break;

    case `freeText`:
      if (element.customText) {
        ctx.fillStyle = element.textColor || `#1c1c1e`;
        ctx.font = `14px Inter, system-ui, sans-serif`;
        ctx.textAlign = `left`;
        ctx.textBaseline = `top`;
        drawWrappedText(ctx, element.customText, x + 8, y + 8, width - 16, 18);
      } else {
        ctx.fillStyle = `#94A3B8`;
        ctx.font = `12px Inter, system-ui, sans-serif`;
        ctx.textAlign = `center`;
        ctx.textBaseline = `middle`;
        ctx.fillText(`Free Text`, x + width / 2, y + height / 2);
      }
      break;

    case `video`: {
      ctx.fillStyle = `#0F172A`;
      ctx.beginPath();
      ctx.rect(x, y, width, height);
      ctx.fill();
      ctx.strokeStyle = `#334155`;
      ctx.stroke();
      const vcx = x + width / 2;
      const vcy = y + height / 2;
      const vr = Math.min(width, height) * 0.22;
      ctx.beginPath();
      ctx.arc(vcx, vcy, vr, 0, Math.PI * 2);
      ctx.strokeStyle = `#94a3b8`;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.beginPath();
      const triSize = vr * 0.6;
      ctx.moveTo(vcx - triSize * 0.4, vcy - triSize);
      ctx.lineTo(vcx + triSize * 0.8, vcy);
      ctx.lineTo(vcx - triSize * 0.4, vcy + triSize);
      ctx.closePath();
      ctx.fillStyle = `#94a3b8`;
      ctx.fill();
      break;
    }

    case `sketch`:
      ctx.fillStyle = `rgba(255,255,255,0)`;
      (element.sketchPoints || []).forEach((stroke) => {
        if (stroke.length < 2) return;
        ctx.beginPath();
        ctx.moveTo(x + stroke[0].x, y + stroke[0].y);
        stroke.slice(1).forEach((p) => {
          ctx.lineTo(x + p.x, y + p.y);
        });
        ctx.strokeStyle = `#1c1c1e`;
        ctx.lineWidth = 2;
        ctx.lineCap = `round`;
        ctx.lineJoin = `round`;
        ctx.stroke();
      });
      break;

    case `group`:
      if (element.groupChildren) {
        const { originalWidth, originalHeight } = getGroupChildrenBounds(
          element.groupChildren,
        );
        const scaleX = width / originalWidth;
        const scaleY = height / originalHeight;
        // eslint-disable-next-line no-restricted-syntax
        for (const child of element.groupChildren) {
          // eslint-disable-next-line no-await-in-loop
          await drawWireframeElement(ctx, {
            ...child,
            x: x + Math.round(child.x * scaleX),
            y: y + Math.round(child.y * scaleY),
            width: Math.round(child.width * scaleX),
            height: Math.round(child.height * scaleY),
          });
        }
      }
      break;

    default:
      ctx.fillStyle = definition.fillColor;
      drawRoundedRect(ctx, x, y, width, height, 4);
      ctx.fill();
      if (definition.strokeColor !== `transparent`) {
        ctx.strokeStyle = definition.strokeColor;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
  }

  ctx.restore();
};

const renderLayoutToFile = async (
  elements: CanvasElement[],
  width: number,
  height: number,
): Promise<File> => {
  const canvas = document.createElement(`canvas`);
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext(`2d`);
  if (!ctx) throw new Error(`Canvas 2D context unavailable`);

  ctx.fillStyle = `#FFFFFF`;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const visibleElements = elements.filter((el) => el.visible);
  // eslint-disable-next-line no-restricted-syntax
  for (const element of visibleElements) {
    // eslint-disable-next-line no-await-in-loop
    await drawWireframeElement(ctx, element);
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error(`Failed to export canvas as image`));
        return;
      }
      const file = new File([blob], `layout.png`, {
        type: `image/png`,
        lastModified: Date.now(),
      });
      resolve(file);
    }, `image/png`);
  });
};

export interface ToolWhiteboardImageEditorProps {
  onGenerateItClicked?: (img: File) => void;
  setIsWhiteboardOpen?: (open: boolean) => void;
}

export function ToolWhiteboardImageEditor({
  onGenerateItClicked,
  setIsWhiteboardOpen,
}: ToolWhiteboardImageEditorProps) {
  const {
    devicePreview: {
      editorState: { device },
    },
  } = useContext(EditorContext);

  const isDesktop = device === DeviceType.Desktop;
  const defaultW = isDesktop ? CANVAS_WIDTH_DESKTOP : CANVAS_WIDTH_MOBILE;
  const defaultH = isDesktop ? CANVAS_HEIGHT_DESKTOP : CANVAS_HEIGHT_MOBILE;

  const [elements, setElements] = useState<CanvasElement[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [multiSelectedIds, setMultiSelectedIds] = useState<Set<string>>(
    new Set(),
  );
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [elementStart, setElementStart] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [activePanel, setActivePanel] = useState<PanelType>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [canvasWidth, setCanvasWidth] = useState(defaultW);
  const [canvasHeight, setCanvasHeight] = useState(defaultH);
  const [canvasWidthInput, setCanvasWidthInput] = useState(String(defaultW));
  const [canvasHeightInput, setCanvasHeightInput] = useState(String(defaultH));
  const [marquee, setMarquee] = useState<{
    startX: number;
    startY: number;
    endX: number;
    endY: number;
  } | null>(null);
  const [isMultiDragging, setIsMultiDragging] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const undoStack = useRef<CanvasElement[][]>([]);
  const redoStack = useRef<CanvasElement[][]>([]);
  const multiDragStartRef = useRef<Map<string, { x: number; y: number }>>(
    new Map(),
  );
  const [isSketchMode, setIsSketchMode] = useState(false);
  const [activeSketchId, setActiveSketchId] = useState<string | null>(null);
  const isSketchDrawingRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [snapGuides, setSnapGuides] = useState<{
    x: number | null;
    y: number | null;
  }>({ x: null, y: null });

  const pushUndo = useCallback((snapshot: CanvasElement[]) => {
    undoStack.current = [...undoStack.current, snapshot];
    redoStack.current = [];
  }, []);

  const undo = useCallback(() => {
    if (undoStack.current.length === 0) return;
    const prev = undoStack.current[undoStack.current.length - 1];
    undoStack.current = undoStack.current.slice(0, -1);
    redoStack.current = [...redoStack.current, elements];
    setElements(prev);
    setSelectedId(null);
  }, [elements]);

  const redo = useCallback(() => {
    if (redoStack.current.length === 0) return;
    const next = redoStack.current[redoStack.current.length - 1];
    redoStack.current = redoStack.current.slice(0, -1);
    undoStack.current = [...undoStack.current, elements];
    setElements(next);
    setSelectedId(null);
  }, [elements]);

  const selectedElement = elements.find((el) => el.id === selectedId) ?? null;

  const onClose = useCallback(() => {
    setIsWhiteboardOpen(false);
  }, [setIsWhiteboardOpen]);

  const togglePanel = useCallback(
    (panel: 'layers' | 'canvasSize') => {
      setActivePanel((prev) => {
        const next = prev === panel ? null : panel;
        if (next === `canvasSize`) {
          setCanvasWidthInput(String(canvasWidth));
          setCanvasHeightInput(String(canvasHeight));
        }
        return next;
      });
    },
    [canvasWidth, canvasHeight],
  );

  const getElementDimensions = useCallback(
    (type: string, definition: BlockDefinition) => {
      if (type === `overlay`)
        return { width: canvasWidth, height: canvasHeight };
      if (type === `divider`)
        return {
          width: Math.round(canvasWidth * 0.9),
          height: definition.defaultHeight,
        };
      return {
        width: definition.defaultWidth,
        height: definition.defaultHeight,
      };
    },
    [canvasWidth, canvasHeight],
  );

  const getElementPosition = (type: string) =>
    type === `overlay`
      ? { x: 0, y: 0 }
      : { x: 50 + Math.random() * 100, y: 50 + Math.random() * 100 };

  const addElement = useCallback(
    (type: string) => {
      const definition = getBlockDefinition(type);
      const elementId = uuidv4();
      const { width, height } = getElementDimensions(type, definition);
      const { x, y } = getElementPosition(type);
      const newElement: CanvasElement = {
        id: elementId,
        type,
        x,
        y,
        width,
        height,
        visible: true,
        locked: false,
        name: `${definition.label} ${elementId.slice(0, 4)}`,
      };
      pushUndo(elements);
      setElements((prev) => [...prev, newElement]);
      setSelectedId(newElement.id);
    },
    [elements, pushUndo, getElementDimensions],
  );

  const updateElement = useCallback(
    (id: string, updates: Partial<CanvasElement>) => {
      setElements((prev) =>
        prev.map((el) => (el.id === id ? { ...el, ...updates } : el)),
      );
    },
    [],
  );

  const deleteElement = useCallback(
    (id: string) => {
      pushUndo(elements);
      setElements((prev) => prev.filter((el) => el.id !== id));
      setSelectedId((prev) => (prev === id ? null : prev));
    },
    [elements, pushUndo],
  );

  const duplicateElement = useCallback(
    (id: string) => {
      const element = elements.find((el) => el.id === id);
      if (!element) return;

      pushUndo(elements);
      const newElement: CanvasElement = {
        ...element,
        id: uuidv4(),
        x: element.x + 20,
        y: element.y + 20,
        name: `${element.name} copy`,
      };
      setElements((prev) => [...prev, newElement]);
      setSelectedId(newElement.id);
    },
    [elements, pushUndo],
  );

  const sendToBack = useCallback(
    (id: string) => {
      pushUndo(elements);
      setElements((prev) => {
        const index = prev.findIndex((el) => el.id === id);
        if (index <= 0) return prev;
        const element = prev[index];
        const newElements = prev.filter((el) => el.id !== id);
        return [element, ...newElements];
      });
    },
    [elements, pushUndo],
  );

  const ungroupElement = useCallback(
    (id: string) => {
      const element = elements.find((el) => el.id === id);
      if (!element || !canUngroup(element.type)) return;

      pushUndo(elements);

      if (element.type === `group` && element.groupChildren) {
        const { originalWidth, originalHeight } = getGroupChildrenBounds(
          element.groupChildren,
        );
        const groupScaleX = element.width / originalWidth;
        const groupScaleY = element.height / originalHeight;
        const restoredChildren: CanvasElement[] = element.groupChildren.map(
          (child) => ({
            ...child,
            id: uuidv4(),
            x: element.x + Math.round(child.x * groupScaleX),
            y: element.y + Math.round(child.y * groupScaleY),
            width: Math.round(child.width * groupScaleX),
            height: Math.round(child.height * groupScaleY),
          }),
        );
        setElements((prev) => [
          ...prev.filter((el) => el.id !== id),
          ...restoredChildren,
        ]);
        setSelectedId(null);
        return;
      }

      const parentDef = getBlockDefinition(element.type);
      const scaleX = element.width / parentDef.defaultWidth;
      const scaleY = element.height / parentDef.defaultHeight;

      const children = UNGROUP_MAP[element.type];
      const newElements: CanvasElement[] = children.map((child) => {
        const childId = uuidv4();
        const def = getBlockDefinition(child.type);
        const childX = child.useRatio
          ? element.x + Math.round(child.xOffset * element.width)
          : element.x + Math.round(child.xOffset * scaleX);
        const childY = child.useRatio
          ? element.y + Math.round(child.yOffset * element.height)
          : element.y + Math.round(child.yOffset * scaleY);
        const childW = child.useRatio
          ? Math.round(child.width * element.width)
          : Math.round(child.width * scaleX);
        const childH = child.useRatio
          ? Math.round(child.height * element.height)
          : Math.round(child.height * scaleY);
        return {
          id: childId,
          type: child.type,
          x: childX,
          y: childY,
          width: childW,
          height: childH,
          visible: true,
          locked: false,
          name: `${def.label} ${childId.slice(0, 4)}`,
        };
      });

      if (element.type === `overlay`) {
        setElements((prev) => {
          const without = prev.filter((el) => el.id !== id);
          return [...without, ...newElements];
        });
      } else {
        const bgId = uuidv4();
        const backgroundElement: CanvasElement = {
          id: bgId,
          type: element.type,
          x: element.x,
          y: element.y,
          width: element.width,
          height: element.height,
          visible: true,
          locked: false,
          name: `${parentDef.label} BG`,
        };

        setElements((prev) => {
          const without = prev.filter((el) => el.id !== id);
          return [...without, backgroundElement, ...newElements];
        });
      }
      setSelectedId(null);
    },
    [elements, pushUndo],
  );

  const groupSelectedElements = useCallback(() => {
    if (multiSelectedIds.size < 2) return;
    pushUndo(elements);

    const selectedElements = elements.filter((el) =>
      multiSelectedIds.has(el.id),
    );
    const minX = Math.min(...selectedElements.map((el) => el.x));
    const minY = Math.min(...selectedElements.map((el) => el.y));
    const maxX = Math.max(...selectedElements.map((el) => el.x + el.width));
    const maxY = Math.max(...selectedElements.map((el) => el.y + el.height));

    const groupId = uuidv4();
    const storedChildren = selectedElements.map((el) => ({
      ...el,
      x: el.x - minX,
      y: el.y - minY,
    }));

    const groupElement: CanvasElement = {
      id: groupId,
      type: `group`,
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
      visible: true,
      locked: false,
      name: `Group ${groupId.slice(0, 4)}`,
      groupChildren: storedChildren,
    };

    setElements((prev) => [
      ...prev.filter((el) => !multiSelectedIds.has(el.id)),
      groupElement,
    ]);
    setSelectedId(groupId);
    setMultiSelectedIds(new Set());
  }, [elements, multiSelectedIds, pushUndo]);

  const startMultiDrag = useCallback(
    (clientX: number, clientY: number) => {
      setIsMultiDragging(true);
      setDragStart({ x: clientX, y: clientY });
      const startPositions = new Map<string, { x: number; y: number }>();
      elements.forEach((el) => {
        if (multiSelectedIds.has(el.id)) {
          startPositions.set(el.id, { x: el.x, y: el.y });
        }
      });
      multiDragStartRef.current = startPositions;
    },
    [elements, multiSelectedIds],
  );

  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!canvasRef.current) return;
      setContextMenu(null);

      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      if (isSketchMode) {
        const sketchId = uuidv4();
        const newSketch: CanvasElement = {
          id: sketchId,
          type: `sketch`,
          x: 0,
          y: 0,
          width: canvasWidth,
          height: canvasHeight,
          visible: true,
          locked: false,
          name: `Sketch ${sketchId.slice(0, 4)}`,
          sketchPoints: [[{ x, y }]],
        };
        pushUndo(elements);
        setElements((prev) => [...prev, newSketch]);
        setActiveSketchId(sketchId);
        isSketchDrawingRef.current = true;
        return;
      }

      const clickedElement = findTopVisibleUnlockedElementAtPosition(
        elements,
        x,
        y,
      );

      if (clickedElement && e.shiftKey) {
        const newIds = new Set(multiSelectedIds);
        if (selectedId && selectedId !== clickedElement.id) {
          newIds.add(selectedId);
        }
        if (newIds.has(clickedElement.id)) {
          newIds.delete(clickedElement.id);
        } else {
          newIds.add(clickedElement.id);
        }
        if (newIds.size > 0) {
          setSelectedId(null);
          setMultiSelectedIds(newIds);
        } else {
          setSelectedId(clickedElement.id);
          setMultiSelectedIds(new Set());
        }
        setMarquee(null);
      } else if (clickedElement && multiSelectedIds.has(clickedElement.id)) {
        startMultiDrag(e.clientX, e.clientY);
        setMarquee(null);
      } else if (clickedElement) {
        setSelectedId(clickedElement.id);
        setMultiSelectedIds(new Set());
        setMarquee(null);
        setIsDragging(true);
        setDragStart({ x: e.clientX, y: e.clientY });
        setElementStart({
          x: clickedElement.x,
          y: clickedElement.y,
          width: clickedElement.width,
          height: clickedElement.height,
        });
      } else {
        if (!e.shiftKey) {
          setSelectedId(null);
          setMultiSelectedIds(new Set());
        }
        setMarquee({ startX: x, startY: y, endX: x, endY: y });
      }
    },
    [
      elements,
      multiSelectedIds,
      startMultiDrag,
      isSketchMode,
      canvasWidth,
      canvasHeight,
      pushUndo,
    ],
  );

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!canvasRef.current) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const clickedElement = [...elements]
        .reverse()
        .find(
          (el) =>
            el.visible &&
            x >= el.x &&
            x <= el.x + el.width &&
            y >= el.y &&
            y <= el.y + el.height,
        );

      if (clickedElement) {
        if (!multiSelectedIds.has(clickedElement.id)) {
          setSelectedId(clickedElement.id);
          setMultiSelectedIds(new Set());
        }
        setContextMenu({
          x: e.clientX,
          y: e.clientY,
          elementId: clickedElement.id,
        });
      }
    },
    [elements, multiSelectedIds],
  );

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, handle: ResizeHandle) => {
      e.stopPropagation();
      if (!selectedElement || selectedElement.locked) return;

      setIsResizing(true);
      setResizeHandle(handle);
      setDragStart({ x: e.clientX, y: e.clientY });
      setElementStart({
        x: selectedElement.x,
        y: selectedElement.y,
        width: selectedElement.width,
        height: selectedElement.height,
      });
    },
    [selectedElement],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isSketchDrawingRef.current && activeSketchId && canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        const sx = e.clientX - rect.left;
        const sy = e.clientY - rect.top;
        setElements((prev) =>
          prev.map((el) => {
            if (el.id !== activeSketchId) return el;
            const strokes = el.sketchPoints ? [...el.sketchPoints] : [[]];
            const lastStroke = [
              ...strokes[strokes.length - 1],
              { x: sx, y: sy },
            ];
            return {
              ...el,
              sketchPoints: [...strokes.slice(0, -1), lastStroke],
            };
          }),
        );
        return;
      }

      if (marquee && canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;
        setMarquee((prev) => (prev ? { ...prev, endX: mx, endY: my } : null));
        return;
      }

      if (isMultiDragging && multiSelectedIds.size > 0) {
        const dx = e.clientX - dragStart.x;
        const dy = e.clientY - dragStart.y;
        setElements((prev) =>
          prev.map((el) => {
            const start = multiDragStartRef.current.get(el.id);
            if (!start) return el;
            return {
              ...el,
              x: Math.max(0, Math.min(canvasWidth - el.width, start.x + dx)),
              y: Math.max(0, Math.min(canvasHeight - el.height, start.y + dy)),
            };
          }),
        );
        return;
      }

      if (!selectedId) return;

      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;

      if (isDragging && !isResizing) {
        let newX = Math.max(
          0,
          Math.min(canvasWidth - elementStart.width, elementStart.x + dx),
        );
        let newY = Math.max(
          0,
          Math.min(canvasHeight - elementStart.height, elementStart.y + dy),
        );

        const elCenterX = newX + elementStart.width / 2;
        const elCenterY = newY + elementStart.height / 2;
        const canvasCenterX = canvasWidth / 2;
        const canvasCenterY = canvasHeight / 2;

        let snapX: number | null = null;
        let snapY: number | null = null;

        if (Math.abs(elCenterX - canvasCenterX) < SNAP_THRESHOLD) {
          newX = canvasCenterX - elementStart.width / 2;
          snapX = canvasCenterX;
        }
        if (Math.abs(elCenterY - canvasCenterY) < SNAP_THRESHOLD) {
          newY = canvasCenterY - elementStart.height / 2;
          snapY = canvasCenterY;
        }

        updateElement(selectedId, { x: newX, y: newY });
        setSnapGuides({ x: snapX, y: snapY });
      }

      if (isResizing && resizeHandle) {
        let newX = elementStart.x;
        let newY = elementStart.y;
        let newWidth = elementStart.width;
        let newHeight = elementStart.height;

        if (resizeHandle.includes(`e`)) {
          newWidth = Math.max(MIN_SIZE, elementStart.width + dx);
        }
        if (resizeHandle.includes(`w`)) {
          const widthDelta = Math.min(dx, elementStart.width - MIN_SIZE);
          newWidth = elementStart.width - widthDelta;
          newX = elementStart.x + widthDelta;
        }
        if (resizeHandle.includes(`s`)) {
          newHeight = Math.max(MIN_SIZE, elementStart.height + dy);
        }
        if (resizeHandle.includes(`n`)) {
          const heightDelta = Math.min(dy, elementStart.height - MIN_SIZE);
          newHeight = elementStart.height - heightDelta;
          newY = elementStart.y + heightDelta;
        }

        newX = Math.max(0, newX);
        newY = Math.max(0, newY);
        newWidth = Math.min(newWidth, canvasWidth - newX);
        newHeight = Math.min(newHeight, canvasHeight - newY);

        updateElement(selectedId, {
          x: newX,
          y: newY,
          width: newWidth,
          height: newHeight,
        });
      }
    },
    [
      selectedId,
      isDragging,
      isResizing,
      resizeHandle,
      dragStart,
      elementStart,
      updateElement,
      canvasWidth,
      canvasHeight,
      marquee,
      isMultiDragging,
      multiSelectedIds,
      activeSketchId,
    ],
  );

  const handleMouseUp = useCallback(() => {
    if (isSketchDrawingRef.current) {
      isSketchDrawingRef.current = false;
      const finishedId = activeSketchId;
      setActiveSketchId(null);
      setIsSketchMode(false);
      setSelectedId(null);

      if (finishedId) {
        setElements((prev) =>
          prev.map((el) => {
            if (el.id !== finishedId || !el.sketchPoints) return el;
            const allPoints = el.sketchPoints.flat();
            if (allPoints.length === 0) return el;
            const { minX, minY, maxX, maxY } =
              calculatePointsBoundingBox(allPoints);
            const bx = minX - SKETCH_PADDING;
            const by = minY - SKETCH_PADDING;
            const bw = maxX - minX + SKETCH_PADDING * 2;
            const bh = maxY - minY + SKETCH_PADDING * 2;
            const adjustedStrokes = el.sketchPoints.map((stroke) =>
              stroke.map((p) => ({ x: p.x - bx, y: p.y - by })),
            );
            return {
              ...el,
              x: bx,
              y: by,
              width: bw,
              height: bh,
              sketchPoints: adjustedStrokes,
            };
          }),
        );
      }
    }
    if (marquee) {
      const bounds = getMarqueeBounds(marquee);
      const right = bounds.left + bounds.width;
      const bottom = bounds.top + bounds.height;

      if (bounds.width > MIN_MARQUEE_DRAG || bounds.height > MIN_MARQUEE_DRAG) {
        const ids = new Set<string>();
        elements.forEach((el) => {
          if (!el.visible) return;
          const elRight = el.x + el.width;
          const elBottom = el.y + el.height;
          if (
            el.x < right &&
            elRight > bounds.left &&
            el.y < bottom &&
            elBottom > bounds.top
          ) {
            ids.add(el.id);
          }
        });
        if (ids.size === 1) {
          setSelectedId([...ids][0]);
          setMultiSelectedIds(new Set());
        } else if (ids.size > 1) {
          setSelectedId(null);
          setMultiSelectedIds(ids);
        }
      }
      setMarquee(null);
    }
    if (isMultiDragging) {
      setIsMultiDragging(false);
      multiDragStartRef.current = new Map();
    }
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
    setSnapGuides({ x: null, y: null });
  }, [marquee, elements, isMultiDragging, activeSketchId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === `INPUT` || tag === `TEXTAREA`) return;

      const lowerKey = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && lowerKey === `z` && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      if (
        (e.metaKey || e.ctrlKey) &&
        (lowerKey === `y` || (lowerKey === `z` && e.shiftKey))
      ) {
        e.preventDefault();
        redo();
        return;
      }

      if (e.key === `Delete` || e.key === `Backspace`) {
        if (selectedId) {
          deleteElement(selectedId);
        } else if (multiSelectedIds.size > 0) {
          pushUndo(elements);
          setElements((prev) =>
            prev.filter((el) => !multiSelectedIds.has(el.id)),
          );
          setMultiSelectedIds(new Set());
        }
        return;
      }

      if (ARROW_KEYS.includes(e.key)) {
        const step = e.shiftKey ? 10 : 1;
        const dx =
          // eslint-disable-next-line no-nested-ternary
          e.key === `ArrowLeft` ? -step : e.key === `ArrowRight` ? step : 0;
        const dy =
          // eslint-disable-next-line no-nested-ternary
          e.key === `ArrowUp` ? -step : e.key === `ArrowDown` ? step : 0;

        if (selectedId) {
          e.preventDefault();
          const el = elements.find((item) => item.id === selectedId);
          if (el && !el.locked) {
            pushUndo(elements);
            updateElement(selectedId, {
              x: Math.max(0, Math.min(canvasWidth - el.width, el.x + dx)),
              y: Math.max(0, Math.min(canvasHeight - el.height, el.y + dy)),
            });
          }
        } else if (multiSelectedIds.size > 0) {
          e.preventDefault();
          pushUndo(elements);
          setElements((prev) =>
            prev.map((el) => {
              if (!multiSelectedIds.has(el.id) || el.locked) return el;
              return {
                ...el,
                x: Math.max(0, Math.min(canvasWidth - el.width, el.x + dx)),
                y: Math.max(0, Math.min(canvasHeight - el.height, el.y + dy)),
              };
            }),
          );
        }
      }
    };
    window.addEventListener(`keydown`, handleKeyDown);
    return () => window.removeEventListener(`keydown`, handleKeyDown);
  }, [
    selectedId,
    multiSelectedIds,
    elements,
    deleteElement,
    pushUndo,
    updateElement,
    canvasWidth,
    canvasHeight,
    undo,
    redo,
  ]);

  const handleImageUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !selectedId) return;
      const capturedId = selectedId;
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        const img = new Image();
        img.onload = () => {
          if (img.height === 0 || img.width === 0) return;
          setElements((prev) => {
            const el = prev.find((item) => item.id === capturedId);
            if (!el) return prev;
            const ratio = img.width / img.height;
            const newWidth = el.width;
            const newHeight = Math.round(newWidth / ratio);
            return prev.map((item) =>
              item.id === capturedId
                ? {
                    ...item,
                    imageDataUrl: dataUrl,
                    width: newWidth,
                    height: Math.max(MIN_SIZE, newHeight),
                  }
                : item,
            );
          });
        };
        img.src = dataUrl;
      };
      reader.readAsDataURL(file);
      e.target.value = ``;
    },
    [selectedId],
  );

  const handleDoubleClick = useCallback(
    (elementId: string, elementType: string) => {
      if (elementType === `freeText`) {
        setEditingTextId(elementId);
      }
    },
    [],
  );

  const shiftStepKeyDown = useCallback(
    (
        field: `x` | `y` | `width` | `height`,
        currentValue: number,
        minValue = 0,
      ) =>
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.shiftKey && (e.key === `ArrowUp` || e.key === `ArrowDown`)) {
          e.preventDefault();
          const delta = e.key === `ArrowUp` ? 10 : -10;
          updateElement(selectedId!, {
            [field]: Math.max(minValue, Math.round(currentValue) + delta),
          });
        }
      },
    [selectedId, updateElement],
  );

  const onGenerate = useCallback(async () => {
    if (elements.length === 0) return;
    setLoading(true);
    try {
      const file = await renderLayoutToFile(
        elements,
        canvasWidth,
        canvasHeight,
      );
      onGenerateItClicked?.(file);
      setIsWhiteboardOpen(false);
    } finally {
      setLoading(false);
    }
  }, [
    elements,
    onGenerateItClicked,
    setIsWhiteboardOpen,
    canvasWidth,
    canvasHeight,
  ]);

  return (
    <Wrapper>
      <FigmaToolbar>
        <ToolbarSection>
          <ToolbarTitle>Design a layout</ToolbarTitle>
        </ToolbarSection>
        <ToolbarSection>
          <ToolButton
            onClick={undo}
            title="Undo"
            disabled={undoStack.current.length === 0}
          >
            <IoArrowUndoOutline size={18} />
          </ToolButton>
          <ToolButton
            onClick={redo}
            title="Redo"
            disabled={redoStack.current.length === 0}
          >
            <IoArrowRedoOutline size={18} />
          </ToolButton>
          <ToolButton
            $active={isSketchMode}
            onClick={() => setIsSketchMode((prev) => !prev)}
            title="Sketch"
          >
            <IconSketch />
          </ToolButton>
          <ToolButton
            $active={activePanel === `canvasSize`}
            onClick={() => togglePanel(`canvasSize`)}
            title="Canvas size"
          >
            <IoResizeOutline size={18} />
          </ToolButton>
          <ToolButton
            $active={activePanel === `layers`}
            onClick={() => togglePanel(`layers`)}
            title="Layers"
          >
            <IoLayersOutline size={18} />
          </ToolButton>
          <ToolButton onClick={onClose} title="Close">
            <IoCloseSharp size={18} />
          </ToolButton>
        </ToolbarSection>
      </FigmaToolbar>

      <Workspace ref={workspaceRef}>
        {selectedElement && (
          <PropertiesBar>
            <PropertyRow>
              {isTextEditableType(selectedElement.type) && (
                <PropertyGroup style={{ flex: 2 }}>
                  <PropertyInput
                    type="text"
                    value={selectedElement.customText || ``}
                    onChange={(e) =>
                      updateElement(selectedElement.id, {
                        customText: e.target.value,
                      })
                    }
                    placeholder="Text"
                  />
                </PropertyGroup>
              )}
              {selectedElement.type === `freeText` && (
                <PropertyGroup>
                  <ColorInput
                    type="color"
                    value={selectedElement.textColor || `#1c1c1e`}
                    onChange={(e) =>
                      updateElement(selectedElement.id, {
                        textColor: e.target.value,
                      })
                    }
                  />
                </PropertyGroup>
              )}
              {selectedElement.type === `uploadImage` && (
                <PropertyGroup>
                  <UploadBtn
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <IconUploadImage />
                    <span>Upload Image</span>
                  </UploadBtn>
                </PropertyGroup>
              )}
              <PropertyGroup>
                <PropertyInput
                  type="number"
                  value={Math.round(selectedElement.x)}
                  onChange={(e) =>
                    updateElement(selectedElement.id, {
                      x: parseInt(e.target.value, 10) || 0,
                    })
                  }
                  onKeyDown={shiftStepKeyDown(`x`, selectedElement.x)}
                  step={1}
                  placeholder="X"
                  style={{ width: `52px` }}
                />
              </PropertyGroup>
              <PropertyGroup>
                <PropertyInput
                  type="number"
                  value={Math.round(selectedElement.y)}
                  onChange={(e) =>
                    updateElement(selectedElement.id, {
                      y: parseInt(e.target.value, 10) || 0,
                    })
                  }
                  onKeyDown={shiftStepKeyDown(`y`, selectedElement.y)}
                  step={1}
                  placeholder="Y"
                  style={{ width: `52px` }}
                />
              </PropertyGroup>
              <PropertyGroup>
                <PropertyInput
                  type="number"
                  value={Math.round(selectedElement.width)}
                  onChange={(e) =>
                    updateElement(selectedElement.id, {
                      width: Math.max(
                        MIN_SIZE,
                        parseInt(e.target.value, 10) || MIN_SIZE,
                      ),
                    })
                  }
                  onKeyDown={shiftStepKeyDown(
                    `width`,
                    selectedElement.width,
                    MIN_SIZE,
                  )}
                  step={1}
                  placeholder="W"
                  style={{ width: `52px` }}
                />
              </PropertyGroup>
              <PropertyGroup>
                <PropertyInput
                  type="number"
                  value={Math.round(selectedElement.height)}
                  onChange={(e) =>
                    updateElement(selectedElement.id, {
                      height: Math.max(
                        MIN_SIZE,
                        parseInt(e.target.value, 10) || MIN_SIZE,
                      ),
                    })
                  }
                  onKeyDown={shiftStepKeyDown(
                    `height`,
                    selectedElement.height,
                    MIN_SIZE,
                  )}
                  step={1}
                  placeholder="H"
                  style={{ width: `52px` }}
                />
              </PropertyGroup>
              {canUngroup(selectedElement.type) && (
                <PropertyGroup>
                  <UngroupBtn
                    type="button"
                    onClick={() => ungroupElement(selectedElement.id)}
                    title="Ungroup into sub-elements"
                  >
                    <IoGridOutline size={14} />
                    <span>Ungroup</span>
                  </UngroupBtn>
                </PropertyGroup>
              )}
            </PropertyRow>
          </PropertiesBar>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: `none` }}
          onChange={handleImageUpload}
        />
        <CanvasViewport
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedId(null);
              setMultiSelectedIds(new Set());
              setContextMenu(null);
            }
          }}
        >
          <CanvasWrapper
            ref={canvasRef}
            $width={canvasWidth}
            $height={canvasHeight}
            style={isSketchMode ? { cursor: `crosshair` } : undefined}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onContextMenu={handleContextMenu}
          >
            {elements.length === 0 && (
              <EmptyCanvasHint>
                <span>+</span>
                <small>Add blocks from below</small>
              </EmptyCanvasHint>
            )}
            {elements.map((element) => {
              const definition = getBlockDefinition(element.type);
              const isSelected =
                element.id === selectedId || multiSelectedIds.has(element.id);

              if (!element.visible) return null;

              return (
                <CanvasElementStyled
                  key={element.id}
                  style={{
                    left: element.x,
                    top: element.y,
                    width: element.width,
                    height: element.height,
                    ...(element.type === `sketch` && !isSelected
                      ? { pointerEvents: `none` as const }
                      : {}),
                  }}
                  $isSelected={isSelected}
                  $isLocked={element.locked}
                  onMouseDown={(e) => {
                    if (isSketchMode) return;
                    if (element.locked) {
                      e.stopPropagation();
                      setSelectedId(element.id);
                      setMultiSelectedIds(new Set());
                      return;
                    }
                    if (
                      element.id === selectedId &&
                      element.type === `overlay`
                    ) {
                      e.stopPropagation();
                      setSelectedId(null);
                      setMultiSelectedIds(new Set());
                      return;
                    }
                    if (e.shiftKey) {
                      return;
                    }
                    if (multiSelectedIds.has(element.id)) {
                      e.stopPropagation();
                      startMultiDrag(e.clientX, e.clientY);
                      return;
                    }
                    setSelectedId(element.id);
                    setMultiSelectedIds(new Set());
                    setIsDragging(true);
                    setDragStart({ x: e.clientX, y: e.clientY });
                    setElementStart({
                      x: element.x,
                      y: element.y,
                      width: element.width,
                      height: element.height,
                    });
                  }}
                  onDoubleClick={() =>
                    handleDoubleClick(element.id, element.type)
                  }
                >
                  {editingTextId === element.id &&
                  element.type === `freeText` ? (
                    <FreeTextEditArea
                      autoFocus
                      value={element.customText || ``}
                      style={{ color: element.textColor || `#1c1c1e` }}
                      onChange={(e) =>
                        updateElement(element.id, {
                          customText: e.target.value,
                        })
                      }
                      onBlur={() => setEditingTextId(null)}
                      onKeyDown={(e) => {
                        if (e.key === `Escape`) setEditingTextId(null);
                        e.stopPropagation();
                      }}
                    />
                  ) : (
                    renderElementContent(element, definition)
                  )}
                  {isSelected && !element.locked && (
                    <>
                      <ResizeHandleNW
                        onMouseDown={(e) => handleResizeStart(e, `nw`)}
                      />
                      <ResizeHandleN
                        onMouseDown={(e) => handleResizeStart(e, `n`)}
                      />
                      <ResizeHandleNE
                        onMouseDown={(e) => handleResizeStart(e, `ne`)}
                      />
                      <ResizeHandleE
                        onMouseDown={(e) => handleResizeStart(e, `e`)}
                      />
                      <ResizeHandleSE
                        onMouseDown={(e) => handleResizeStart(e, `se`)}
                      />
                      <ResizeHandleS
                        onMouseDown={(e) => handleResizeStart(e, `s`)}
                      />
                      <ResizeHandleSW
                        onMouseDown={(e) => handleResizeStart(e, `sw`)}
                      />
                      <ResizeHandleW
                        onMouseDown={(e) => handleResizeStart(e, `w`)}
                      />
                    </>
                  )}
                </CanvasElementStyled>
              );
            })}
            {snapGuides.x !== null && (
              <div
                style={{
                  position: `absolute`,
                  left: snapGuides.x,
                  top: 0,
                  bottom: 0,
                  width: 1,
                  background: SNAP_GUIDE_COLOR,
                  opacity: 0.5,
                  pointerEvents: `none`,
                  zIndex: 999,
                }}
              />
            )}
            {snapGuides.y !== null && (
              <div
                style={{
                  position: `absolute`,
                  top: snapGuides.y,
                  left: 0,
                  right: 0,
                  height: 1,
                  background: SNAP_GUIDE_COLOR,
                  opacity: 0.5,
                  pointerEvents: `none`,
                  zIndex: 999,
                }}
              />
            )}
            {marquee &&
              (() => {
                const bounds = getMarqueeBounds(marquee);
                return (
                  <MarqueeRect
                    style={{
                      left: bounds.left,
                      top: bounds.top,
                      width: bounds.width,
                      height: bounds.height,
                    }}
                  />
                );
              })()}
          </CanvasWrapper>
        </CanvasViewport>

        {contextMenu &&
          (() => {
            const contextElement = elements.find(
              (e) => e.id === contextMenu.elementId,
            );
            if (!contextElement) return null;
            return (
              <ContextMenuOverlay onClick={() => setContextMenu(null)}>
                <ContextMenuWrapper
                  style={{ left: contextMenu.x + 8, top: contextMenu.y + 8 }}
                >
                  <ContextMenuItem
                    onClick={() => {
                      duplicateElement(contextMenu.elementId);
                      setContextMenu(null);
                    }}
                  >
                    <IoCopyOutline size={14} />
                    <span>Duplicate</span>
                  </ContextMenuItem>
                  <ContextMenuItem
                    onClick={() => {
                      sendToBack(contextMenu.elementId);
                      setContextMenu(null);
                    }}
                  >
                    <IoUnlinkOutline size={14} />
                    <span>Send to back</span>
                  </ContextMenuItem>
                  {canUngroup(contextElement.type) && (
                    <ContextMenuItem
                      onClick={() => {
                        ungroupElement(contextMenu.elementId);
                        setContextMenu(null);
                      }}
                    >
                      <IoGridOutline size={14} />
                      <span>Ungroup</span>
                    </ContextMenuItem>
                  )}
                  {multiSelectedIds.size > 1 && (
                    <ContextMenuItem
                      onClick={() => {
                        groupSelectedElements();
                        setContextMenu(null);
                      }}
                    >
                      <IoGridOutline size={14} />
                      <span>Group</span>
                    </ContextMenuItem>
                  )}
                  <ContextMenuItem
                    onClick={() => {
                      updateElement(contextMenu.elementId, {
                        locked: !contextElement.locked,
                      });
                      setContextMenu(null);
                    }}
                  >
                    <IoLockClosedOutline size={14} />
                    <span>{contextElement.locked ? `Unlock` : `Lock`}</span>
                  </ContextMenuItem>
                  <ContextMenuItem
                    onClick={() => {
                      updateElement(contextMenu.elementId, {
                        visible: !contextElement.visible,
                      });
                      setContextMenu(null);
                    }}
                  >
                    <IoEyeOffOutline size={14} />
                    <span>{contextElement.visible ? `Hide` : `Show`}</span>
                  </ContextMenuItem>
                  <ContextMenuDivider />
                  <ContextMenuItem
                    $danger
                    onClick={() => {
                      deleteElement(contextMenu.elementId);
                      setContextMenu(null);
                    }}
                  >
                    <IoTrashOutline size={14} />
                    <span>Delete</span>
                  </ContextMenuItem>
                </ContextMenuWrapper>
              </ContextMenuOverlay>
            );
          })()}

        {activePanel === `layers` && (
          <FloatingPanel $position="left">
            <PanelHeader>
              <span>Layers</span>
              <PanelCloseBtn type="button" onClick={() => setActivePanel(null)}>
                <IoCloseSharp size={14} />
              </PanelCloseBtn>
            </PanelHeader>
            <PanelBody>
              <LayersList>
                {elements.length === 0 ? (
                  <EmptyLayers>No layers yet</EmptyLayers>
                ) : (
                  [...elements].reverse().map((element) => (
                    <LayerItem
                      key={element.id}
                      element={element}
                      isSelected={element.id === selectedId}
                      onSelect={() => setSelectedId(element.id)}
                      onToggleVisibility={() =>
                        updateElement(element.id, {
                          visible: !element.visible,
                        })
                      }
                      onToggleLock={() =>
                        updateElement(element.id, { locked: !element.locked })
                      }
                      onDelete={() => deleteElement(element.id)}
                      onDuplicate={() => duplicateElement(element.id)}
                      onSendToBack={() => sendToBack(element.id)}
                    />
                  ))
                )}
              </LayersList>
            </PanelBody>
          </FloatingPanel>
        )}

        {activePanel === `canvasSize` && (
          <FloatingPanel $position="right">
            <PanelHeader>
              <span>Canvas size</span>
              <PanelCloseBtn type="button" onClick={() => setActivePanel(null)}>
                <IoCloseSharp size={14} />
              </PanelCloseBtn>
            </PanelHeader>
            <PanelBody>
              <PropertyRow>
                <PropertyGroup style={{ flex: 1 }}>
                  <PropertyLabel>Width</PropertyLabel>
                  <PropertyInput
                    type="number"
                    value={canvasWidthInput}
                    onChange={(e) => setCanvasWidthInput(e.target.value)}
                    onBlur={() => {
                      const v = Math.max(
                        MIN_CANVAS_SIZE,
                        parseInt(canvasWidthInput, 10) || MIN_CANVAS_SIZE,
                      );
                      setCanvasWidth(v);
                      setCanvasWidthInput(String(v));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === `Enter`) {
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                  />
                </PropertyGroup>
                <PropertyGroup style={{ flex: 1 }}>
                  <PropertyLabel>Height</PropertyLabel>
                  <PropertyInput
                    type="number"
                    value={canvasHeightInput}
                    onChange={(e) => setCanvasHeightInput(e.target.value)}
                    onBlur={() => {
                      const v = Math.max(
                        MIN_CANVAS_SIZE,
                        parseInt(canvasHeightInput, 10) || MIN_CANVAS_SIZE,
                      );
                      setCanvasHeight(v);
                      setCanvasHeightInput(String(v));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === `Enter`) {
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                  />
                </PropertyGroup>
              </PropertyRow>
            </PanelBody>
          </FloatingPanel>
        )}
      </Workspace>

      <BlocksStrip>
        <BlocksStripLabel>Add blocks</BlocksStripLabel>
        <BlocksStripScroll>
          {BLOCKS_STRIP_ORDER.map((t) =>
            BLOCK_DEFINITIONS.find((d) => d.type === t),
          )
            .filter((d): d is BlockDefinition => Boolean(d))
            .map((def) => (
              <PanelBlockItem
                key={def.type}
                definition={def}
                onAdd={addElement}
              />
            ))}
        </BlocksStripScroll>
      </BlocksStrip>

      <BottomActionBar>
        <DiscardButton disabled={loading} onClick={onClose}>
          Discard
        </DiscardButton>
        <GenerateButton
          disabled={loading || elements.length === 0}
          onClick={onGenerate}
        >
          Generate
        </GenerateButton>
      </BottomActionBar>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: #f2f2f7;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display',
    'SF Pro Text', Inter, system-ui, sans-serif;
  overflow: hidden;
`;

const FigmaToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 44px;
  padding: 0 12px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  z-index: 10;
`;

const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const ToolbarTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1c1c1e;
  white-space: nowrap;
`;

const ToolButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: ${(p) => (p.$active ? `#007aff` : `transparent`)};
  color: ${(p) => (p.$active ? `#fff` : `#007aff`)};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(p) => (p.$active ? `#0066d6` : `rgba(0, 122, 255, 0.1)`)};
  }

  &:active {
    transform: scale(0.92);
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
    pointer-events: none;
  }
`;

const PropertiesBar = styled.div`
  position: fixed;
  top: 44px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  gap: 6px;
  z-index: 20;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
`;

const UngroupBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 7px 10px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  background: rgba(0, 122, 255, 0.06);
  color: #007aff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(0, 122, 255, 0.12);
  }

  &:active {
    transform: scale(0.96);
  }
`;

const Workspace = styled.div`
  flex: 1;
  position: relative;
  overflow: auto;
  min-height: 0;
`;

const CanvasViewport = styled.div`
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e5e5ea;
  background-image: radial-gradient(circle, #c7c7cc 1px, transparent 1px);
  background-size: 20px 20px;
  padding: 40px 24px;
`;

const FloatingPanel = styled.div<{ $position: 'left' | 'right' }>`
  position: fixed;
  top: 100px;
  ${(p) => (p.$position === `left` ? `left: 8px` : `right: 8px`)};
  width: ${(p) => (p.$position === `left` ? `280px` : `220px`)};
  max-height: calc(100vh - 200px);
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 15;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;

  span {
    font-size: 13px;
    font-weight: 600;
    color: #1c1c1e;
  }
`;

const PanelCloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 11px;
  background: rgba(0, 0, 0, 0.06);
  color: #8e8e93;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: #1c1c1e;
  }
`;

const PanelBody = styled.div`
  padding: 8px;
  overflow-y: auto;
  flex: 1;
`;

const BottomActionBar = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px 16px;
  padding-bottom: max(10px, env(safe-area-inset-bottom, 10px));
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
`;

const DiscardButton = styled.button`
  flex: 1;
  padding: 14px 20px;
  border: none;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.05);
  color: #1c1c1e;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.08);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const GenerateButton = styled.button`
  flex: 1;
  padding: 14px 20px;
  border: none;
  border-radius: 12px;
  background: #007aff;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #0066d6;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PanelBlock = styled.div<{ $strokeColor: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 10px 8px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.04);
  cursor: pointer;
  user-select: none;
  transition: all 0.15s ease;
  min-width: 64px;
  flex-shrink: 0;

  &:hover {
    background: rgba(0, 122, 255, 0.08);
  }

  &:active {
    background: rgba(0, 122, 255, 0.15);
    transform: scale(0.95);
  }

  .icon {
    font-size: 16px;
    line-height: 1;
  }

  .label {
    font-size: 10px;
    font-weight: 500;
    color: #8e8e93;
    text-align: center;
    line-height: 1.2;
  }
`;

const LayersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const EmptyLayers = styled.div`
  color: #8e8e93;
  font-size: 13px;
  text-align: center;
  padding: 16px;
`;

const LayerRow = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  background: ${(p) => (p.$isSelected ? `#007aff` : `transparent`)};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${(p) => (p.$isSelected ? `#007aff` : `rgba(0, 0, 0, 0.04)`)};
  }

  &:active {
    transform: scale(0.98);
  }

  .layer-icon {
    font-size: 12px;
    flex-shrink: 0;
  }

  .layer-name {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: ${(p) => (p.$isSelected ? `#ffffff` : `#1c1c1e`)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .layer-actions {
    display: flex;
    gap: 2px;
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  &:hover .layer-actions {
    opacity: 1;
  }

  .layer-action-btn {
    appearance: none;
    border: none;
    background: transparent;
    color: ${(p) => (p.$isSelected ? `rgba(255,255,255,0.7)` : `#8e8e93`)};
    cursor: pointer;
    padding: 3px;
    border-radius: 6px;
    display: flex;
    align-items: center;

    &:hover {
      color: ${(p) => (p.$isSelected ? `#ffffff` : `#1c1c1e`)};
      background: rgba(0, 0, 0, 0.06);
    }

    &.delete:hover {
      color: #ff3b30;
    }
  }
`;

const CanvasWrapper = styled.div<{ $width: number; $height: number }>`
  position: relative;
  width: ${(p) => p.$width}px;
  height: ${(p) => p.$height}px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1), 0 1px 4px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
`;

const EmptyCanvasHint = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #c7c7cc;
  text-align: center;
  padding: 2rem;
  pointer-events: none;

  span {
    font-size: 2.5rem;
    line-height: 1;
    font-weight: 300;
  }

  small {
    font-size: 14px;
    margin-top: 8px;
    color: #8e8e93;
  }
`;

const CanvasElementStyled = styled.div<{
  $isSelected: boolean;
  $isLocked: boolean;
}>`
  position: absolute;
  cursor: ${(p) => (p.$isLocked ? `not-allowed` : `move`)};
  outline: ${(p) => (p.$isSelected ? `2px solid #007aff` : `none`)};
  outline-offset: -1px;
  border-radius: 4px;
  overflow: hidden;

  &:hover {
    outline: ${(p) =>
      p.$isSelected ? `2px solid #007aff` : `1px solid rgba(0, 122, 255, 0.3)`};
  }
`;

const MarqueeRect = styled.div`
  position: absolute;
  border: 1.5px dashed #007aff;
  background: rgba(0, 122, 255, 0.06);
  pointer-events: none;
  z-index: 100;
`;

const ResizeHandleBase = styled.div`
  position: absolute;
  width: 8px;
  height: 8px;
  background: #007aff;
  border: 1.5px solid #ffffff;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
`;

const ResizeHandleNW = styled(ResizeHandleBase)`
  top: -4px;
  left: -4px;
  cursor: nwse-resize;
`;

const ResizeHandleN = styled(ResizeHandleBase)`
  top: -4px;
  left: 50%;
  transform: translateX(-50%);
  cursor: ns-resize;
`;

const ResizeHandleNE = styled(ResizeHandleBase)`
  top: -4px;
  right: -4px;
  cursor: nesw-resize;
`;

const ResizeHandleE = styled(ResizeHandleBase)`
  top: 50%;
  right: -4px;
  transform: translateY(-50%);
  cursor: ew-resize;
`;

const ResizeHandleSE = styled(ResizeHandleBase)`
  bottom: -4px;
  right: -4px;
  cursor: nwse-resize;
`;

const ResizeHandleS = styled(ResizeHandleBase)`
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  cursor: ns-resize;
`;

const ResizeHandleSW = styled(ResizeHandleBase)`
  bottom: -4px;
  left: -4px;
  cursor: nesw-resize;
`;

const ResizeHandleW = styled(ResizeHandleBase)`
  top: 50%;
  left: -4px;
  transform: translateY(-50%);
  cursor: ew-resize;
`;

const PropertyGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PropertyLabel = styled.div`
  font-size: 11px;
  color: #8e8e93;
  font-weight: 500;
`;

const PropertyInput = styled.input`
  width: 100%;
  font-size: 12px;
  color: #1c1c1e;
  padding: 5px 7px;
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  outline: none;
  transition: border-color 0.15s ease;

  &:focus {
    border-color: #007aff;
    background: #fff;
  }
`;

const PropertyRow = styled.div`
  display: flex;
  gap: 8px;
`;

const WireframeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border-radius: 4px;

  .header-text {
    width: 50%;
    height: 10px;
    background: #cbd5e1;
    border-radius: 2px;
  }

  .header-custom-text {
    font-size: 14px;
    font-weight: 600;
    color: #334155;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const WireframeNav = styled.div`
  display: flex;
  align-items: center;
  padding: 6px;
  gap: 6px;
  background: #ffffff;
  border-radius: 4px;

  .nav-item {
    flex: 1;
    height: 100%;
    background: #e2e8f0;
    border-radius: 4px;

    &.active {
      background: #3b82f6;
    }
  }
`;

const WireframeImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  border-radius: 4px;

  .image-icon {
    color: #94a3b8;
  }
`;

const WireframeText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${TEXT_PADDING_Y}px 14px;
  gap: ${TEXT_LINE_GAP}px;
  background: #ffffff;
  border-radius: 4px;
  overflow: hidden;

  .text-row {
    display: flex;
    align-items: center;
  }

  .text-bar {
    width: ${TEXT_BAR_WIDTH_RATIO * 100}%;
    height: ${TEXT_LINE_HEIGHT}px;
    background: #cbd5e1;
    border-radius: 2px;
  }

  .text-row:last-child .text-bar {
    width: ${TEXT_BAR_LAST_WIDTH_RATIO * 100}%;
  }
`;

const WireframeButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: #3b82f6;
  border-radius: 6px;
  color: #ffffff;
  font-weight: 600;
  font-size: 12px;
`;

const WireframeProduct = styled.div`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 6px;
  overflow: hidden;

  .product-image {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f1f5f9;
    color: #94a3b8;
    min-height: 60%;
  }

  .product-info {
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .product-title {
    height: 10px;
    width: 70%;
    background: #cbd5e1;
    border-radius: 2px;
  }

  .product-price {
    height: 10px;
    width: 40%;
    background: #64748b;
    border-radius: 2px;
  }

  .product-button {
    height: 22px;
    background: #3b82f6;
    border-radius: 4px;
    margin-top: 4px;
  }
`;

const WireframeAccordion = styled.div`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 4px;

  .accordion-item {
    border-bottom: 1px solid #e2e8f0;

    &:last-child {
      border-bottom: none;
    }
  }

  .accordion-header {
    display: flex;
    align-items: center;
    padding: 8px 12px;
  }

  .accordion-title {
    flex: 1;
    height: 10px;
    background: #cbd5e1;
    border-radius: 2px;
    max-width: 90%;
  }

  .accordion-icon {
    color: #94a3b8;
    font-size: 10px;
  }
`;

const WireframeOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 4px;

  .overlay-content {
    width: 85%;
    height: 80%;
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    padding: 12px;
    position: relative;
    display: flex;
    flex-direction: column;
  }

  .overlay-close {
    position: absolute;
    top: 8px;
    right: 12px;
    color: #94a3b8;
    font-size: 16px;
    cursor: pointer;
  }

  .overlay-title {
    height: 12px;
    width: 50%;
    background: #cbd5e1;
    border-radius: 2px;
    margin-bottom: 8px;
  }

  .overlay-body {
    flex: 1;
    background: #e2e8f0;
    border-radius: 4px;
    margin-bottom: 8px;
  }

  .overlay-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }

  .overlay-btn {
    height: 26px;
    border-radius: 4px;

    &.secondary {
      width: 50px;
      background: #e2e8f0;
    }

    &.primary {
      width: 60px;
      background: #3b82f6;
    }
  }
`;

const WireframeDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  .divider-line {
    width: 100%;
    height: 1px;
    background: #cbd5e1;
  }
`;

const WireframeHero = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  border-radius: 6px;

  .hero-text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 16px;
    width: 100%;
  }

  .hero-title {
    height: 14px;
    width: 70%;
    background: #cbd5e1;
    border-radius: 2px;
  }

  .hero-subtitle {
    height: 10px;
    width: 50%;
    background: #94a3b8;
    border-radius: 2px;
  }

  .hero-cta {
    height: 28px;
    width: 80px;
    background: #3b82f6;
    border-radius: 4px;
    margin-top: 4px;
  }
`;

const WireframeForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #ffffff;
  border-radius: 4px;

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .form-label {
    height: 8px;
    width: 30%;
    background: #94a3b8;
    border-radius: 2px;
  }

  .form-input {
    height: 28px;
    background: #f1f5f9;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
  }

  .form-submit {
    height: 28px;
    background: #3b82f6;
    border-radius: 4px;
    margin-top: 4px;
  }
`;

const WireframeUgc = styled.div`
  background: #f8fafc;
  border-radius: 4px;
  overflow: hidden;

  .ugc-grid {
    display: flex;
    flex-wrap: wrap;
  }

  .ugc-cell {
    background: #e2e8f0;
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
  }
`;

const WireframeSearch = styled.div`
  display: flex;
  align-items: center;
  padding: 0 14px;
  background: #ffffff;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  gap: 8px;

  .search-icon {
    color: #94a3b8;
    font-size: 14px;
    flex-shrink: 0;
  }

  .search-placeholder {
    height: 8px;
    width: 40%;
    background: #cbd5e1;
    border-radius: 2px;
  }
`;

const WireframeTestimonials = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;

  .testimonial-avatar {
    width: 28px;
    height: 28px;
    border-radius: 14px;
    background: #cbd5e1;
  }

  .testimonial-text {
    height: 8px;
    width: 80%;
    background: #e2e8f0;
    border-radius: 2px;
  }

  .testimonial-stars {
    color: #f59e0b;
    font-size: 10px;
    letter-spacing: 2px;
  }

  .testimonial-name {
    height: 6px;
    width: 30%;
    background: #94a3b8;
    border-radius: 2px;
  }
`;

const WireframeProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  padding: 6px;
  background: #ffffff;
  border-radius: 4px;

  .grid-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .grid-img {
    height: 60%;
    background: #f1f5f9;
    border-radius: 4px;
  }

  .grid-title {
    height: 6px;
    width: 70%;
    background: #cbd5e1;
    border-radius: 2px;
  }

  .grid-price {
    height: 6px;
    width: 40%;
    background: #64748b;
    border-radius: 2px;
  }
`;

const WireframeProductSlider = styled.div`
  display: flex;
  align-items: center;
  background: #ffffff;
  border-radius: 4px;
  overflow: hidden;
  position: relative;

  .slider-arrow {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #475569;
    cursor: default;
    background: linear-gradient(
      to right,
      transparent,
      rgba(255, 255, 255, 0.98) 40%
    );
    z-index: 1;
  }

  .slider-cards {
    flex: 1;
    display: flex;
    gap: 10px;
    overflow: hidden;
    padding: 0;
    padding-right: 32px;
  }

  .slider-card {
    flex: 0 0 calc(40% - 7px);
    min-width: 0;
    display: flex;
    flex-direction: column;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

    &.partial {
      flex: 0 0 calc(20% - 3px);
      opacity: 0.6;
      mask-image: linear-gradient(to right, black 20%, transparent 100%);
      -webkit-mask-image: linear-gradient(
        to right,
        black 20%,
        transparent 100%
      );
    }
  }

  .slider-card-img {
    height: 65%;
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid #f1f5f9;
  }

  .img-icon {
    width: 36%;
    height: 36%;
    opacity: 0.6;
  }

  .slider-card-info {
    padding: 8px 10px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
  }

  .slider-card-title {
    height: 8px;
    width: 80%;
    background: #cbd5e1;
    border-radius: 3px;
  }

  .slider-card-price {
    height: 10px;
    width: 40%;
    background: #64748b;
    border-radius: 3px;
  }
`;

const ContextMenuOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 20;
`;

const ContextMenuWrapper = styled.div`
  position: fixed;
  min-width: 160px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 4px;
  overflow: hidden;
`;

const ContextMenuItem = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${(p) => (p.$danger ? `#ff3b30` : `#1c1c1e`)};
  font-size: 14px;
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover {
    background: ${(p) =>
      p.$danger ? `rgba(255, 59, 48, 0.08)` : `rgba(0, 0, 0, 0.04)`};
  }

  &:active {
    background: ${(p) =>
      p.$danger ? `rgba(255, 59, 48, 0.14)` : `rgba(0, 0, 0, 0.08)`};
  }

  span {
    font-weight: 400;
  }
`;

const ContextMenuDivider = styled.div`
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  margin: 4px 8px;
`;

const BlocksStrip = styled.div`
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 8px 12px;
`;

const BlocksStripLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #8e8e93;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const BlocksStripScroll = styled.div`
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 4px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.12);
    border-radius: 2px;
  }
`;

const WireframeSimpleText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${TEXT_PADDING_Y}px 10px;
  gap: ${TEXT_LINE_GAP}px;
  background: #ffffff;
  border-radius: 4px;
  overflow: hidden;

  .simple-bar {
    width: 90%;
    height: ${TEXT_LINE_HEIGHT}px;
    background: #cbd5e1;
    border-radius: 2px;
  }

  .simple-bar:last-child {
    width: 60%;
  }
`;

const WireframeUploadImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  border: 1px dashed #cbd5e1;
  border-radius: 4px;
  overflow: hidden;

  img {
    display: block;
  }

  .upload-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    color: #94a3b8;
  }

  .upload-label {
    font-size: 11px;
    font-weight: 500;
  }
`;

const WireframeFreeText = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 8px;
  background: transparent;
  border: none;
  border-radius: 0;
  font-size: 14px;
  line-height: 1.4;
  word-break: break-word;
  white-space: pre-wrap;
  overflow: hidden;

  .placeholder-text {
    color: #94a3b8;
    font-style: italic;
    font-size: 12px;
  }
`;

const WireframeVideo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0f172a;
  border-radius: 4px;

  .play-icon {
    width: 36%;
    height: 36%;
  }
`;

const FreeTextEditArea = styled.textarea`
  width: 100%;
  height: 100%;
  padding: 8px;
  border: none;
  outline: none;
  resize: none;
  font-size: 14px;
  font-family: inherit;
  line-height: 1.4;
  background: rgba(255, 255, 255, 0.95);
`;

const ColorInput = styled.input`
  width: 36px;
  height: 36px;
  padding: 2px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  background: transparent;
  cursor: pointer;

  &::-webkit-color-swatch-wrapper {
    padding: 2px;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 4px;
  }
`;

const UploadBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 7px 10px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  background: rgba(0, 122, 255, 0.06);
  color: #007aff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(0, 122, 255, 0.12);
  }

  &:active {
    transform: scale(0.96);
  }
`;
